import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Puzzle,
  Search,
  RefreshCw,
  Zap,
  Bitcoin,
  CreditCard,
  Eye,
  Download,
  Copy,
  Star,
  Code,
  Globe,
  Key,
  Webhook,
  Activity,
  CheckCircle,
  Layers,
  GitBranch,
  Lock,
  Plus,
  Edit,
  Trash2,
  Play,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Info,
  Sparkles,
  Shield,
  Clock,
  Users,
  Building2,
  Database,
  Unlink,
  HelpCircle,
  Lightbulb,
  LayoutGrid,
  List,
  FileText,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { TemplateEditModal } from '../../components/providers/TemplateEditModal';
import { QuickStartPanel } from '../../components/providers/QuickStartPanel';
import { CompletenessBar } from '../../components/providers/CompletenessBar';
import { VisibilityBadge, VisibilityTooltip, VisibilityFilter } from '../../components/providers/VisibilityComponents';

// ============================================
// TYPES
// ============================================

interface ProviderTemplate {
  id: string;
  name: string;
  description?: string;
  type: string;
  category: 'BAAS' | 'CRYPTO' | 'CARDS';
  version: string;
  status: 'DRAFT' | 'TESTING' | 'VALIDATED' | 'PUBLISHED' | 'DEPRECATED';
  baseUrl?: string;
  authConfig: {
    type: string;
    headerName?: string;
    prefix?: string;
    usernameField?: string;
    passwordField?: string;
    apiKeyHeader?: string;
    signatureParam?: string;
    timestampParam?: string;
    tokenUrl?: string;
    grantType?: string;
  };
  credentialSchema: {
    type: string;
    required: string[];
    properties: Record<string, { type: string; title: string; description: string; format?: string }>;
  };
  operations: Record<string, {
    method: string;
    path: string;
    description: string;
    requestMapping?: Record<string, string>;
    responseMapping?: Record<string, string>;
    headers?: Record<string, string>;
    timeout?: number;
    retryConfig?: { maxRetries: number; backoffMs: number };
  }>;
  webhookConfig?: {
    events: string[];
    signatureHeader?: string;
    signatureAlgorithm?: string;
    eventMapping?: Record<string, string>;
  };
  chainConfig?: Record<string, unknown>;
  contractConfig?: Record<string, unknown>;
  dexConfig?: {
    routerAddress?: string;
    factoryAddress?: string;
    supportedChains?: string[];
    supportedTokens?: string[];
  };
  isPublic: boolean;
  rating?: number;
  downloads: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  previousVersionId?: string;
  _count?: { instances: number };
  // Extended fields for documentation
  documentation?: string;
  documentationUrl?: string;
  setupGuide?: string;
  codeExamples?: CodeExample[];
  useCases?: UseCase[];
  changelog?: ChangelogEntry[];
  faq?: FaqEntry[];
  knownLimitations?: string[];
  environmentRequirements?: EnvironmentRequirement[];
  complementaryTemplates?: string[];
}

interface CodeExample {
  id: string;
  language: 'typescript' | 'python' | 'curl' | 'javascript' | 'java' | 'csharp' | 'go' | 'php';
  title: string;
  description: string;
  code: string;
  operation?: string;
}

interface UseCase {
  id: string;
  title: string;
  description: string;
  businessScenario: string;
  recommendedFor: string[];
  icon?: string;
}

interface ChangelogEntry {
  version: string;
  date: string;
  changes: string[];
}

interface FaqEntry {
  question: string;
  answer: string;
}

interface EnvironmentRequirement {
  environment: 'sandbox' | 'production';
  requirements: string[];
  notes?: string;
}

interface ProviderInstance {
  id: string;
  name: string;
  status: string;
  tenant: { id: string; name: string };
  createdAt: string;
  lastUsedAt?: string;
  requestCount?: number;
}

// ============================================
// CONSTANTS
// ============================================

const CATEGORY_OPTIONS = [
  { value: '', label: 'Todas as Categorias' },
  { value: 'BAAS', label: '🏦 BaaS (Banking)' },
  { value: 'CRYPTO', label: '₿ Crypto' },
  { value: 'CARDS', label: '💳 Cartões' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os Status' },
  { value: 'DRAFT', label: '📝 Rascunho' },
  { value: 'TESTING', label: '🧪 Em Teste' },
  { value: 'VALIDATED', label: '✅ Validado' },
  { value: 'PUBLISHED', label: '🚀 Publicado' },
  { value: 'DEPRECATED', label: '⚠️ Descontinuado' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Todos os Tipos' },
  { value: 'REST_API', label: '🌐 REST API' },
  { value: 'GRAPHQL', label: '◈ GraphQL' },
  { value: 'BLOCKCHAIN_EVM', label: '⛓️ Blockchain EVM' },
  { value: 'BLOCKCHAIN_SOLANA', label: '☀️ Blockchain Solana' },
  { value: 'DEX_GENERIC', label: '🔄 DEX Genérico' },
  { value: 'WEBSOCKET', label: '⚡ WebSocket' },
  { value: 'WEBHOOK_RECEIVER', label: '📡 Webhook Receiver' },
];

const VISIBILITY_OPTIONS = [
  { value: '', label: 'Todas Visibilidades' },
  { value: 'public', label: '🌐 Públicos' },
  { value: 'private', label: '🔒 Privados' },
];

const AUTH_TYPE_INFO: Record<string, { label: string; icon: typeof Key; color: string; description: string }> = {
  API_KEY: { label: 'API Key', icon: Key, color: 'text-blue-400', description: 'Autenticação via chave de API no header' },
  BASIC_AUTH: { label: 'Basic Auth', icon: Lock, color: 'text-yellow-400', description: 'Usuário e senha codificados em Base64' },
  BEARER_TOKEN: { label: 'Bearer Token', icon: Shield, color: 'text-green-400', description: 'Token JWT ou OAuth no header Authorization' },
  OAUTH2: { label: 'OAuth 2.0', icon: Globe, color: 'text-purple-400', description: 'Fluxo completo OAuth 2.0 com refresh token' },
  HMAC_SIGNATURE: { label: 'HMAC Signature', icon: Shield, color: 'text-orange-400', description: 'Assinatura HMAC para validação de requests' },
  NONE: { label: 'Sem Autenticação', icon: Unlink, color: 'text-surface-400', description: 'API pública sem autenticação' },
};

const CATEGORY_INFO: Record<string, { label: string; icon: typeof Zap; gradient: string; description: string; emoji: string }> = {
  BAAS: { 
    label: 'Banking as a Service', 
    icon: Zap, 
    gradient: 'from-primary-500 to-accent-500',
    description: 'Pix, Boleto, Transferências, Contas',
    emoji: '🏦'
  },
  CRYPTO: { 
    label: 'Criptomoedas', 
    icon: Bitcoin, 
    gradient: 'from-yellow-500 to-orange-500',
    description: 'Exchanges, Wallets, DeFi, NFTs',
    emoji: '₿'
  },
  CARDS: { 
    label: 'Cartões', 
    icon: CreditCard, 
    gradient: 'from-blue-500 to-indigo-500',
    description: 'Emissão, Pagamentos, Tokenização',
    emoji: '💳'
  },
};

const STATUS_INFO: Record<string, { label: string; color: string; bgColor: string; description: string; emoji: string }> = {
  DRAFT: { label: 'Rascunho', color: 'text-surface-400', bgColor: 'bg-surface-500/20', description: 'Em desenvolvimento', emoji: '📝' },
  TESTING: { label: 'Em Teste', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', description: 'Sendo validado', emoji: '🧪' },
  VALIDATED: { label: 'Validado', color: 'text-blue-400', bgColor: 'bg-blue-500/20', description: 'Pronto para publicar', emoji: '✅' },
  PUBLISHED: { label: 'Publicado', color: 'text-green-400', bgColor: 'bg-green-500/20', description: 'Disponível para uso', emoji: '🚀' },
  DEPRECATED: { label: 'Descontinuado', color: 'text-red-400', bgColor: 'bg-red-500/20', description: 'Não recomendado', emoji: '⚠️' },
};

const TYPE_INFO: Record<string, { label: string; icon: typeof Globe; color: string; description: string }> = {
  REST_API: { label: 'REST API', icon: Globe, color: 'text-green-400', description: 'API RESTful tradicional' },
  GRAPHQL: { label: 'GraphQL', icon: Code, color: 'text-pink-400', description: 'Query language flexível' },
  BLOCKCHAIN_EVM: { label: 'Blockchain EVM', icon: Layers, color: 'text-purple-400', description: 'Ethereum, Polygon, BSC...' },
  BLOCKCHAIN_SOLANA: { label: 'Blockchain Solana', icon: Layers, color: 'text-cyan-400', description: 'Solana Network' },
  DEX_GENERIC: { label: 'DEX Genérico', icon: Activity, color: 'text-orange-400', description: 'Uniswap, PancakeSwap...' },
  WEBSOCKET: { label: 'WebSocket', icon: Zap, color: 'text-yellow-400', description: 'Conexão em tempo real' },
  WEBHOOK_RECEIVER: { label: 'Webhook Receiver', icon: Webhook, color: 'text-blue-400', description: 'Recebe eventos externos' },
};

// ============================================
// MAIN COMPONENT
// ============================================

export function SuperAdminProviderTemplates() {
  // State
  const [templates, setTemplates] = useState<ProviderTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ProviderTemplate | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<ProviderTemplate | null>(null);
  const [editIsDirty, setEditIsDirty] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editIsSaving, setEditIsSaving] = useState(false);
  const [editActiveTab, setEditActiveTab] = useState<'basic' | 'auth' | 'operations' | 'webhooks' | 'credentials' | 'blockchain' | 'preview'>('basic');
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [templateInstances, setTemplateInstances] = useState<ProviderInstance[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'auth' | 'operations' | 'webhooks' | 'credentials' | 'blockchain' | 'instances'>('overview');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedOperations, setExpandedOperations] = useState<Set<string>>(new Set());
  
  // Clone Confirmation Modal State
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneTargetTemplate, setCloneTargetTemplate] = useState<ProviderTemplate | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [isCloning, setIsCloning] = useState(false);

  // Documentation Modal State (Enhanced Edit Modal)
  const [showDocumentationModal, setShowDocumentationModal] = useState(false);
  const [templateCompleteness, setTemplateCompleteness] = useState<{
    score: number;
    missingFields: string[];
    suggestions: string[];
  } | null>(null);

  // New Template Modal State
  const [showNewTemplateModal, setShowNewTemplateModal] = useState(false);
  const [newTemplateStep, setNewTemplateStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [newTemplateData, setNewTemplateData] = useState<{
    name: string;
    description: string;
    category: string;
    type: string;
    version: string;
    baseUrl: string;
    status: string;
    isPublic: boolean;
    authConfig: {
      type: string;
      headerName: string;
      prefix: string;
      apiKeyHeader: string;
      tokenUrl: string;
      grantType: string;
    };
    operations: Record<string, {
      method: string;
      path: string;
      description: string;
      requestMapping?: Record<string, string>;
      responseMapping?: Record<string, string>;
      headers?: Record<string, string>;
      timeout?: number;
      retryConfig?: { maxRetries: number; backoffMs: number };
    }>;
    webhookConfig: {
      events: string[];
      signatureHeader: string;
      signatureAlgorithm: string;
      eventMapping: Record<string, string>;
    };
    credentialSchema: {
      type: string;
      required: string[];
      properties: Record<string, { type: string; title: string; description: string; format?: string }>;
    };
  }>({
    name: '',
    description: '',
    category: 'BAAS',
    type: 'REST_API',
    version: '1.0.0',
    baseUrl: '',
    status: 'DRAFT',
    isPublic: false,
    authConfig: {
      type: 'API_KEY',
      headerName: 'Authorization',
      prefix: '',
      apiKeyHeader: 'X-API-Key',
      tokenUrl: '',
      grantType: 'client_credentials',
    },
    operations: {},
    webhookConfig: {
      events: [],
      signatureHeader: 'X-Webhook-Signature',
      signatureAlgorithm: 'sha256',
      eventMapping: {},
    },
    credentialSchema: {
      type: 'object',
      required: ['apiKey'],
      properties: {
        apiKey: { type: 'string', title: 'API Key', description: 'Chave de API para autenticação' },
      },
    },
  });
  const [newTemplateErrors, setNewTemplateErrors] = useState<Record<string, string>>({});
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);

  // Operation Editor State
  const [showOperationEditor, setShowOperationEditor] = useState(false);
  const [operationEditorStep, setOperationEditorStep] = useState<1 | 2 | 3 | 4>(1);
  const [editingOperationName, setEditingOperationName] = useState<string | null>(null);
  const [operationFormData, setOperationFormData] = useState<{
    name: string;
    method: string;
    path: string;
    description: string;
    requestMapping: Record<string, string>;
    responseMapping: Record<string, string>;
    headers: Record<string, string>;
    timeout: number;
    retryConfig: { maxRetries: number; backoffMs: number };
  }>({
    name: '',
    method: 'GET',
    path: '/',
    description: '',
    requestMapping: {},
    responseMapping: {},
    headers: {},
    timeout: 30000,
    retryConfig: { maxRetries: 3, backoffMs: 1000 },
  });
  const [operationErrors, setOperationErrors] = useState<Record<string, string>>({});

  // Credential Schema Editor State
  const [showCredentialEditor, setShowCredentialEditor] = useState(false);
  const [editingCredentialField, setEditingCredentialField] = useState<string | null>(null);
  const [credentialFieldForm, setCredentialFieldForm] = useState<{
    name: string;
    type: string;
    title: string;
    description: string;
    format: string;
    required: boolean;
  }>({
    name: '',
    type: 'string',
    title: '',
    description: '',
    format: '',
    required: true,
  });
  const [credentialFieldErrors, setCredentialFieldErrors] = useState<Record<string, string>>({});

  // Webhook Event Editor State
  const [newWebhookEvent, setNewWebhookEvent] = useState('');

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter) params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (visibilityFilter) params.append('isPublic', visibilityFilter === 'public' ? 'true' : 'false');

      const templatesRes = await api.get(`/provider-templates?${params.toString()}`);
      const data = templatesRes.data;
      setTemplates(data.data || data.templates || data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateInstances = async (templateId: string) => {
    try {
      setLoadingInstances(true);
      const res = await api.get(`/provider-templates/${templateId}/instances`).catch(() => ({ data: [] }));
      setTemplateInstances(res.data || []);
    } catch (error) {
      setTemplateInstances([]);
    } finally {
      setLoadingInstances(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [categoryFilter, statusFilter, typeFilter, visibilityFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debounce);
  }, [search]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleViewDetails = (template: ProviderTemplate) => {
    setSelectedTemplate(template);
    setActiveTab('overview');
    setShowDetailsModal(true);
    fetchTemplateInstances(template.id);
  };

  const handleViewJson = (template: ProviderTemplate) => {
    setSelectedTemplate(template);
    setShowJsonModal(true);
  };

  const handleEdit = (template: ProviderTemplate) => {
    setSelectedTemplate(template);
    setEditFormData(JSON.parse(JSON.stringify(template))); // Deep clone
    setEditIsDirty(false);
    setEditErrors({});
    setEditActiveTab('basic');
    setShowEditModal(true);
  };

  const handleEditFormChange = (field: string, value: unknown) => {
    if (!editFormData) return;
    setEditFormData(prev => {
      if (!prev) return prev;
      const newData = { ...prev };
      // Handle nested fields like authConfig.type
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentKey = parent as keyof ProviderTemplate;
        const parentValue = newData[parentKey];
        if (typeof parentValue === 'object' && parentValue !== null) {
          (newData as Record<string, unknown>)[parent] = {
            ...parentValue,
            [child]: value
          };
        }
      } else {
        (newData as Record<string, unknown>)[field] = value;
      }
      return newData;
    });
    setEditIsDirty(true);
    // Clear error for this field
    if (editErrors[field]) {
      setEditErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateEditForm = (): boolean => {
    if (!editFormData) return false;
    const errors: Record<string, string> = {};
    
    if (!editFormData.name?.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    if (!editFormData.version?.trim()) {
      errors.version = 'Versão é obrigatória';
    }
    if (!editFormData.category) {
      errors.category = 'Categoria é obrigatória';
    }
    if (!editFormData.type) {
      errors.type = 'Tipo é obrigatório';
    }
    if (editFormData.baseUrl && !editFormData.baseUrl.startsWith('http')) {
      errors.baseUrl = 'URL deve começar com http:// ou https://';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!editFormData || !selectedTemplate) return;
    
    if (!validateEditForm()) {
      return;
    }
    
    try {
      setEditIsSaving(true);
      await api.put(`/provider-templates/${selectedTemplate.id}`, editFormData);
      // IMPORTANT: Reset dirty flag BEFORE closing modal to prevent false "unsaved changes" warning
      setEditIsDirty(false);
      setShowEditModal(false);
      setEditFormData(null);
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      setEditErrors({ _general: 'Erro ao salvar template. Tente novamente.' });
    } finally {
      setEditIsSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    if (editIsDirty) {
      setShowUnsavedChangesModal(true);
    } else {
      setShowEditModal(false);
      setEditFormData(null);
    }
  };

  const handleConfirmCloseEdit = () => {
    setShowUnsavedChangesModal(false);
    setShowEditModal(false);
    setEditFormData(null);
    setEditIsDirty(false);
  };

  // Keyboard shortcuts for Edit Modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showEditModal) return;
      
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCloseEditModal();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveEdit();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showEditModal, editIsDirty, editFormData]);

  const handleDelete = (template: ProviderTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await api.delete(`/provider-templates/${selectedTemplate.id}`);
      setShowDeleteModal(false);
      setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleChangeStatus = async (template: ProviderTemplate, newStatus: string) => {
    try {
      await api.post(`/provider-templates/${template.id}/status`, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Error changing status:', error);
    }
  };

  const handleToggleVisibility = async (template: ProviderTemplate) => {
    try {
      await api.post(`/provider-templates/${template.id}/visibility/toggle`);
      fetchData();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const handleClone = (template: ProviderTemplate) => {
    setCloneTargetTemplate(template);
    setCloneName(`${template.name} (Cópia)`);
    setShowCloneModal(true);
  };

  const handleConfirmClone = async () => {
    if (!cloneTargetTemplate || !cloneName.trim()) return;
    
    try {
      setIsCloning(true);
      await api.post(`/provider-templates/${cloneTargetTemplate.id}/clone`, { name: cloneName.trim() });
      setShowCloneModal(false);
      setCloneTargetTemplate(null);
      setCloneName('');
      fetchData();
    } catch (error) {
      console.error('Error cloning template:', error);
    } finally {
      setIsCloning(false);
    }
  };

  const handleCancelClone = () => {
    setShowCloneModal(false);
    setCloneTargetTemplate(null);
    setCloneName('');
  };

  // ============================================
  // DOCUMENTATION MODAL HANDLERS
  // ============================================

  const handleOpenDocumentation = async (template: ProviderTemplate) => {
    setSelectedTemplate(template);
    // Fetch completeness data
    try {
      const res = await api.get(`/provider-templates/${template.id}/completeness`).catch(() => ({ data: null }));
      if (res.data) {
        setTemplateCompleteness(res.data);
      } else {
        // Calculate locally if endpoint doesn't exist
        setTemplateCompleteness(calculateCompleteness(template));
      }
    } catch {
      setTemplateCompleteness(calculateCompleteness(template));
    }
    setShowDocumentationModal(true);
  };

  const calculateCompleteness = (template: ProviderTemplate) => {
    const fields = [
      { name: 'Descrição', filled: !!template.description },
      { name: 'Documentação', filled: !!template.documentation },
      { name: 'URL da Documentação', filled: !!template.documentationUrl },
      { name: 'Guia de Configuração', filled: !!template.setupGuide },
      { name: 'Exemplos de Código', filled: (template.codeExamples?.length || 0) > 0 },
      { name: 'Casos de Uso', filled: (template.useCases?.length || 0) > 0 },
      { name: 'Changelog', filled: (template.changelog?.length || 0) > 0 },
      { name: 'FAQ', filled: (template.faq?.length || 0) > 0 },
      { name: 'Limitações Conhecidas', filled: (template.knownLimitations?.length || 0) > 0 },
      { name: 'Requisitos de Ambiente', filled: (template.environmentRequirements?.length || 0) > 0 },
      { name: 'Templates Complementares', filled: (template.complementaryTemplates?.length || 0) > 0 },
    ];
    
    const filledCount = fields.filter(f => f.filled).length;
    const score = Math.round((filledCount / fields.length) * 100);
    const missingFields = fields.filter(f => !f.filled).map(f => f.name);
    
    const suggestions: string[] = [];
    if (!template.documentation) suggestions.push('Adicione documentação detalhada para ajudar os usuários');
    if ((template.codeExamples?.length || 0) === 0) suggestions.push('Adicione exemplos de código para facilitar a integração');
    if ((template.useCases?.length || 0) === 0) suggestions.push('Defina casos de uso para mostrar aplicações práticas');
    
    return { score, missingFields, suggestions };
  };

  const handleSaveDocumentation = async (data: Partial<ProviderTemplate>) => {
    if (!selectedTemplate) return;
    try {
      await api.put(`/provider-templates/${selectedTemplate.id}`, data);
      fetchData();
    } catch (error) {
      console.error('Error saving documentation:', error);
      throw error;
    }
  };

  const handleToggleVisibilityFromModal = async () => {
    if (!selectedTemplate) return;
    try {
      await api.post(`/provider-templates/${selectedTemplate.id}/visibility/toggle`);
      // Update local state
      setSelectedTemplate(prev => prev ? { ...prev, isPublic: !prev.isPublic } : null);
      fetchData();
    } catch (error) {
      console.error('Error toggling visibility:', error);
      throw error;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleOperation = (opName: string) => {
    const newExpanded = new Set(expandedOperations);
    if (newExpanded.has(opName)) {
      newExpanded.delete(opName);
    } else {
      newExpanded.add(opName);
    }
    setExpandedOperations(newExpanded);
  };

  // ============================================
  // OPERATION EDITOR HANDLERS
  // ============================================

  const resetOperationForm = () => {
    setOperationFormData({
      name: '',
      method: 'GET',
      path: '/',
      description: '',
      requestMapping: {},
      responseMapping: {},
      headers: {},
      timeout: 30000,
      retryConfig: { maxRetries: 3, backoffMs: 1000 },
    });
    setOperationErrors({});
    setOperationEditorStep(1);
    setEditingOperationName(null);
  };

  const handleAddOperation = () => {
    resetOperationForm();
    setShowOperationEditor(true);
  };

  const handleEditOperation = (opName: string) => {
    if (!editFormData?.operations?.[opName]) return;
    const op = editFormData.operations[opName];
    setOperationFormData({
      name: opName,
      method: op.method || 'GET',
      path: op.path || '/',
      description: op.description || '',
      requestMapping: op.requestMapping || {},
      responseMapping: op.responseMapping || {},
      headers: op.headers || {},
      timeout: op.timeout || 30000,
      retryConfig: op.retryConfig || { maxRetries: 3, backoffMs: 1000 },
    });
    setEditingOperationName(opName);
    setOperationEditorStep(1);
    setOperationErrors({});
    setShowOperationEditor(true);
  };

  const handleDeleteOperation = (opName: string) => {
    if (!editFormData) return;
    const newOperations = { ...editFormData.operations };
    delete newOperations[opName];
    handleEditFormChange('operations', newOperations);
  };

  const validateOperationStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    
    if (step === 1) {
      if (!operationFormData.name.trim()) {
        errors.name = 'Nome da operação é obrigatório';
      } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(operationFormData.name)) {
        errors.name = 'Nome deve começar com letra e conter apenas letras, números e _';
      } else if (editingOperationName !== operationFormData.name && editFormData?.operations?.[operationFormData.name]) {
        errors.name = 'Já existe uma operação com este nome';
      }
      if (!operationFormData.method) {
        errors.method = 'Método HTTP é obrigatório';
      }
    }
    
    if (step === 2) {
      if (!operationFormData.path.trim()) {
        errors.path = 'Path é obrigatório';
      } else if (!operationFormData.path.startsWith('/')) {
        errors.path = 'Path deve começar com /';
      } else if (!/^\/[a-zA-Z0-9\-_\/:{}]*$/.test(operationFormData.path)) {
        errors.path = 'Path contém caracteres inválidos';
      }
    }
    
    setOperationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOperationNextStep = () => {
    if (validateOperationStep(operationEditorStep)) {
      setOperationEditorStep((prev) => Math.min(prev + 1, 4) as 1 | 2 | 3 | 4);
    }
  };

  const handleOperationPrevStep = () => {
    setOperationEditorStep((prev) => Math.max(prev - 1, 1) as 1 | 2 | 3 | 4);
  };

  const handleSaveOperation = () => {
    if (!validateOperationStep(1) || !validateOperationStep(2)) {
      return;
    }
    
    if (!editFormData) return;
    
    const newOperations = { ...editFormData.operations };
    
    // If renaming, delete old key
    if (editingOperationName && editingOperationName !== operationFormData.name) {
      delete newOperations[editingOperationName];
    }
    
    newOperations[operationFormData.name] = {
      method: operationFormData.method,
      path: operationFormData.path,
      description: operationFormData.description,
      requestMapping: Object.keys(operationFormData.requestMapping).length > 0 ? operationFormData.requestMapping : undefined,
      responseMapping: Object.keys(operationFormData.responseMapping).length > 0 ? operationFormData.responseMapping : undefined,
      headers: Object.keys(operationFormData.headers).length > 0 ? operationFormData.headers : undefined,
      timeout: operationFormData.timeout,
      retryConfig: operationFormData.retryConfig,
    };
    
    handleEditFormChange('operations', newOperations);
    setShowOperationEditor(false);
    resetOperationForm();
  };

  const handleOperationFormChange = (field: string, value: unknown) => {
    setOperationFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (operationErrors[field]) {
      setOperationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleMappingChange = (type: 'requestMapping' | 'responseMapping' | 'headers', key: string, value: string) => {
    setOperationFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [key]: value,
      },
    }));
  };

  const handleMappingDelete = (type: 'requestMapping' | 'responseMapping' | 'headers', key: string) => {
    setOperationFormData(prev => {
      const newMapping = { ...prev[type] };
      delete newMapping[key];
      return {
        ...prev,
        [type]: newMapping,
      };
    });
  };

  const handleMappingAdd = (type: 'requestMapping' | 'responseMapping' | 'headers') => {
    const newKey = `field_${Object.keys(operationFormData[type]).length + 1}`;
    setOperationFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [newKey]: '',
      },
    }));
  };

  // ============================================
  // CREDENTIAL SCHEMA EDITOR HANDLERS
  // ============================================

  const resetCredentialFieldForm = () => {
    setCredentialFieldForm({
      name: '',
      type: 'string',
      title: '',
      description: '',
      format: '',
      required: true,
    });
    setCredentialFieldErrors({});
    setEditingCredentialField(null);
  };

  const handleAddCredentialField = () => {
    resetCredentialFieldForm();
    setShowCredentialEditor(true);
  };

  const handleEditCredentialField = (fieldName: string) => {
    if (!editFormData?.credentialSchema?.properties?.[fieldName]) return;
    const field = editFormData.credentialSchema.properties[fieldName];
    setCredentialFieldForm({
      name: fieldName,
      type: field.type || 'string',
      title: field.title || '',
      description: field.description || '',
      format: field.format || '',
      required: editFormData.credentialSchema.required?.includes(fieldName) || false,
    });
    setEditingCredentialField(fieldName);
    setCredentialFieldErrors({});
    setShowCredentialEditor(true);
  };

  const handleDeleteCredentialField = (fieldName: string) => {
    if (!editFormData) return;
    const newProperties = { ...editFormData.credentialSchema?.properties };
    delete newProperties[fieldName];
    const newRequired = (editFormData.credentialSchema?.required || []).filter(r => r !== fieldName);
    handleEditFormChange('credentialSchema', {
      ...editFormData.credentialSchema,
      type: 'object',
      properties: newProperties,
      required: newRequired,
    });
  };

  const validateCredentialField = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!credentialFieldForm.name.trim()) {
      errors.name = 'Nome do campo é obrigatório';
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(credentialFieldForm.name)) {
      errors.name = 'Nome deve começar com letra e conter apenas letras, números e _';
    } else if (editingCredentialField !== credentialFieldForm.name && 
               editFormData?.credentialSchema?.properties?.[credentialFieldForm.name]) {
      errors.name = 'Já existe um campo com este nome';
    }
    
    if (!credentialFieldForm.title.trim()) {
      errors.title = 'Título é obrigatório';
    }
    
    setCredentialFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveCredentialField = () => {
    if (!validateCredentialField() || !editFormData) return;
    
    const newProperties = { ...editFormData.credentialSchema?.properties };
    
    // If renaming, delete old key
    if (editingCredentialField && editingCredentialField !== credentialFieldForm.name) {
      delete newProperties[editingCredentialField];
    }
    
    newProperties[credentialFieldForm.name] = {
      type: credentialFieldForm.type,
      title: credentialFieldForm.title,
      description: credentialFieldForm.description,
      ...(credentialFieldForm.format ? { format: credentialFieldForm.format } : {}),
    };
    
    let newRequired = [...(editFormData.credentialSchema?.required || [])];
    
    // Remove old name from required if renaming
    if (editingCredentialField && editingCredentialField !== credentialFieldForm.name) {
      newRequired = newRequired.filter(r => r !== editingCredentialField);
    }
    
    // Add/remove from required based on toggle
    if (credentialFieldForm.required && !newRequired.includes(credentialFieldForm.name)) {
      newRequired.push(credentialFieldForm.name);
    } else if (!credentialFieldForm.required) {
      newRequired = newRequired.filter(r => r !== credentialFieldForm.name);
    }
    
    handleEditFormChange('credentialSchema', {
      type: 'object',
      properties: newProperties,
      required: newRequired,
    });
    
    setShowCredentialEditor(false);
    resetCredentialFieldForm();
  };

  const handleCredentialFieldFormChange = (field: string, value: unknown) => {
    setCredentialFieldForm(prev => ({
      ...prev,
      [field]: value,
    }));
    if (credentialFieldErrors[field]) {
      setCredentialFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // ============================================
  // WEBHOOK EVENT HANDLERS
  // ============================================

  const handleAddWebhookEvent = () => {
    if (!newWebhookEvent.trim() || !editFormData) return;
    const currentEvents = editFormData.webhookConfig?.events || [];
    if (currentEvents.includes(newWebhookEvent.trim())) return;
    const newConfig = {
      ...editFormData.webhookConfig,
      events: [...currentEvents, newWebhookEvent.trim()],
    };
    handleEditFormChange('webhookConfig', newConfig);
    setNewWebhookEvent('');
  };

  // ============================================
  // NEW TEMPLATE HANDLERS
  // ============================================

  const resetNewTemplateForm = () => {
    setNewTemplateData({
      name: '',
      description: '',
      category: 'BAAS',
      type: 'REST_API',
      version: '1.0.0',
      baseUrl: '',
      status: 'DRAFT',
      isPublic: false,
      authConfig: {
        type: 'API_KEY',
        headerName: 'Authorization',
        prefix: '',
        apiKeyHeader: 'X-API-Key',
        tokenUrl: '',
        grantType: 'client_credentials',
      },
      operations: {},
      webhookConfig: {
        events: [],
        signatureHeader: 'X-Webhook-Signature',
        signatureAlgorithm: 'sha256',
        eventMapping: {},
      },
      credentialSchema: {
        type: 'object',
        required: ['apiKey'],
        properties: {
          apiKey: { type: 'string', title: 'API Key', description: 'Chave de API para autenticação' },
        },
      },
    });
    setNewTemplateStep(1);
    setNewTemplateErrors({});
  };

  const handleOpenNewTemplateModal = () => {
    resetNewTemplateForm();
    setShowNewTemplateModal(true);
  };

  const handleCloseNewTemplateModal = () => {
    setShowNewTemplateModal(false);
    resetNewTemplateForm();
  };

  const handleNewTemplateFormChange = (field: string, value: unknown) => {
    setNewTemplateData(prev => {
      const newData = { ...prev };
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        const parentKey = parent as keyof typeof newData;
        const parentValue = newData[parentKey];
        if (typeof parentValue === 'object' && parentValue !== null) {
          (newData as Record<string, unknown>)[parent] = {
            ...parentValue,
            [child]: value
          };
        }
      } else {
        (newData as Record<string, unknown>)[field] = value;
      }
      return newData;
    });
    if (newTemplateErrors[field]) {
      setNewTemplateErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateNewTemplateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    
    if (step === 1) {
      if (!newTemplateData.name?.trim()) {
        errors.name = 'Nome é obrigatório';
      }
      if (!newTemplateData.category) {
        errors.category = 'Categoria é obrigatória';
      }
      if (!newTemplateData.type) {
        errors.type = 'Tipo é obrigatório';
      }
      if (!newTemplateData.version?.trim()) {
        errors.version = 'Versão é obrigatória';
      }
    }
    
    if (step === 2) {
      if (newTemplateData.baseUrl && !newTemplateData.baseUrl.startsWith('http')) {
        errors.baseUrl = 'URL deve começar com http:// ou https://';
      }
      if (!newTemplateData.authConfig?.type) {
        errors['authConfig.type'] = 'Tipo de autenticação é obrigatório';
      }
    }
    
    setNewTemplateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNewTemplateNextStep = () => {
    if (validateNewTemplateStep(newTemplateStep)) {
      setNewTemplateStep((prev) => Math.min(prev + 1, 5) as 1 | 2 | 3 | 4 | 5);
    }
  };

  const handleNewTemplatePrevStep = () => {
    setNewTemplateStep((prev) => Math.max(prev - 1, 1) as 1 | 2 | 3 | 4 | 5);
  };

  const handleCreateTemplate = async () => {
    if (!validateNewTemplateStep(1) || !validateNewTemplateStep(2)) {
      return;
    }
    
    try {
      setIsCreatingTemplate(true);
      await api.post('/provider-templates', newTemplateData);
      setShowNewTemplateModal(false);
      resetNewTemplateForm();
      fetchData();
    } catch (error) {
      console.error('Error creating template:', error);
      setNewTemplateErrors({ _general: 'Erro ao criar template. Tente novamente.' });
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const totalTemplates = templates.length;
  const publishedTemplates = templates.filter(t => t.status === 'PUBLISHED').length;
  const totalInstances = templates.reduce((sum, t) => sum + (t._count?.instances || 0), 0);
  const avgRating = templates.filter(t => t.rating).length > 0
    ? templates.filter(t => t.rating).reduce((sum, t) => sum + (t.rating || 0), 0) / templates.filter(t => t.rating).length
    : 0;

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const getCategoryInfo = (category: string) => CATEGORY_INFO[category] || CATEGORY_INFO.BAAS;
  const getStatusInfo = (status: string) => STATUS_INFO[status] || STATUS_INFO.DRAFT;
  const getTypeInfo = (type: string) => TYPE_INFO[type] || TYPE_INFO.REST_API;
  const getAuthInfo = (authType: string) => AUTH_TYPE_INFO[authType] || AUTH_TYPE_INFO.NONE;

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'text-green-400 bg-green-500/10 border-green-500/30',
      POST: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
      PUT: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
      PATCH: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
      DELETE: 'text-red-400 bg-red-500/10 border-red-500/30',
    };
    return colors[method.toUpperCase()] || 'text-surface-400 bg-surface-500/10 border-surface-500/30';
  };

  const renderStars = (rating: number | undefined) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${i <= Math.round(rating) ? 'text-yellow-500 fill-yellow-500' : 'text-surface-600'}`}
          />
        ))}
        <span className="text-sm text-surface-400 ml-1">{rating.toFixed(1)}</span>
      </div>
    );
  };


  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-6">
      {/* ============================================ */}
      {/* HEADER */}
      {/* ============================================ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-lg shadow-primary-500/25">
              <Puzzle className="w-8 h-8 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-surface-900 border-2 border-primary-500 flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-primary-400" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-2">
              Provider Templates
              <Badge variant="primary" size="sm">{totalTemplates}</Badge>
            </h1>
            <p className="text-surface-400 flex items-center gap-2">
              <span>Blueprints para conectar providers externos</span>
              <button 
                onClick={() => setShowHelpModal(true)}
                className="text-primary-400 hover:text-primary-300 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={fetchData}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="primary" onClick={handleOpenNewTemplateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Template
          </Button>
        </div>
      </div>

      {/* ============================================ */}
      {/* STATS CARDS */}
      {/* ============================================ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-primary-500/10 to-transparent border-primary-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Database className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{totalTemplates}</p>
              <p className="text-sm text-surface-400">Total Templates</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{publishedTemplates}</p>
              <p className="text-sm text-surface-400">Publicados</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{totalInstances}</p>
              <p className="text-sm text-surface-400">Instâncias Ativas</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-yellow-500/10 to-transparent border-yellow-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-surface-100">{avgRating.toFixed(1)}</p>
              <p className="text-sm text-surface-400">Avaliação Média</p>
            </div>
          </div>
        </Card>
      </div>

      {/* ============================================ */}
      {/* CATEGORY CARDS */}
      {/* ============================================ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(CATEGORY_INFO).map(([key, info]) => {
          const count = templates.filter(t => t.category === key).length;
          return (
            <motion.div
              key={key}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card 
                className={`p-4 cursor-pointer transition-all ${categoryFilter === key ? 'ring-2 ring-primary-500' : ''}`}
                onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center`}>
                      <span className="text-2xl">{info.emoji}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-surface-100">{info.label}</p>
                      <p className="text-sm text-surface-400">{info.description}</p>
                    </div>
                  </div>
                  <Badge variant="primary">{count}</Badge>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* ============================================ */}
      {/* FILTERS */}
      {/* ============================================ */}
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Buscar templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={STATUS_OPTIONS}
              className="w-40"
            />
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={TYPE_OPTIONS}
              className="w-44"
            />
            <Select
              value={visibilityFilter}
              onChange={(e) => setVisibilityFilter(e.target.value)}
              options={VISIBILITY_OPTIONS}
              className="w-44"
            />
          </div>
          <div className="flex items-center gap-1 bg-surface-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'text-surface-400 hover:text-surface-200'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-surface-400 hover:text-surface-200'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* ============================================ */}
      {/* TEMPLATES GRID/LIST */}
      {/* ============================================ */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="p-12 text-center">
          <Puzzle className="w-16 h-16 text-surface-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-surface-300 mb-2">Nenhum template encontrado</h3>
          <p className="text-surface-400 mb-4">Crie seu primeiro template para começar</p>
          <Button variant="primary" onClick={handleOpenNewTemplateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Criar Template
          </Button>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {templates.map((template) => {
              const categoryInfo = getCategoryInfo(template.category);
              const statusInfo = getStatusInfo(template.status);
              const typeInfo = getTypeInfo(template.type);
              return (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  layout
                >
                  <Card className="p-4 hover:border-primary-500/50 transition-all group">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${categoryInfo.gradient} flex items-center justify-center`}>
                          <span className="text-lg">{categoryInfo.emoji}</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-surface-100 group-hover:text-primary-400 transition-colors">
                            {template.name}
                          </h3>
                          <p className="text-xs text-surface-500">v{template.version}</p>
                        </div>
                      </div>
                      <Badge className={`${statusInfo.bgColor} ${statusInfo.color}`}>
                        {statusInfo.emoji} {statusInfo.label}
                      </Badge>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-surface-400 mb-3 line-clamp-2">
                      {template.description || `Template ${typeInfo.label} para ${categoryInfo.label}`}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="default" size="sm" className={typeInfo.color}>
                        {typeInfo.label}
                      </Badge>
                      <Badge 
                        variant="default" 
                        size="sm" 
                        className={template.isPublic ? 'text-green-400 bg-green-500/20' : 'text-orange-400 bg-orange-500/20'}
                      >
                        {template.isPublic ? (
                          <><Globe className="w-3 h-3 mr-1" /> Público</>
                        ) : (
                          <><Lock className="w-3 h-3 mr-1" /> Privado</>
                        )}
                      </Badge>
                      <Badge variant="default" size="sm">
                        {Object.keys(template.operations || {}).length} operações
                      </Badge>
                      {template._count?.instances ? (
                        <Badge variant="default" size="sm">
                          <Users className="w-3 h-3 mr-1" />
                          {template._count.instances}
                        </Badge>
                      ) : null}
                    </div>

                    {/* Rating */}
                    {renderStars(template.rating)}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-surface-700">
                      <Button variant="secondary" size="sm" onClick={() => handleViewDetails(template)}>
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleEdit(template)}>
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleOpenDocumentation(template)} title="Documentação e Casos de Uso">
                        <FileText className="w-4 h-4 mr-1" />
                        Docs
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleClone(template)}>
                        <Copy className="w-4 h-4 mr-1" />
                        Clonar
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleToggleVisibility(template)}
                        title={template.isPublic ? 'Tornar privado' : 'Tornar público'}
                        className={template.isPublic ? 'text-green-400 hover:text-green-300' : 'text-orange-400 hover:text-orange-300'}
                      >
                        {template.isPublic ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(template)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        /* LIST VIEW */
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Template</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Categoria</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Visibilidade</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Operações</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Instâncias</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {templates.map((template) => {
                const categoryInfo = getCategoryInfo(template.category);
                const statusInfo = getStatusInfo(template.status);
                const typeInfo = getTypeInfo(template.type);
                return (
                  <tr key={template.id} className="hover:bg-surface-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{categoryInfo.emoji}</span>
                        <div>
                          <p className="font-medium text-surface-100">{template.name}</p>
                          <p className="text-xs text-surface-500">v{template.version}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="default" size="sm">{categoryInfo.label}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${typeInfo.color}`}>{typeInfo.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${statusInfo.bgColor} ${statusInfo.color}`}>
                        {statusInfo.emoji} {statusInfo.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleVisibility(template)}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          template.isPublic 
                            ? 'text-green-400 bg-green-500/20 hover:bg-green-500/30' 
                            : 'text-orange-400 bg-orange-500/20 hover:bg-orange-500/30'
                        }`}
                        title={template.isPublic ? 'Clique para tornar privado' : 'Clique para tornar público'}
                      >
                        {template.isPublic ? (
                          <><Globe className="w-3 h-3" /> Público</>
                        ) : (
                          <><Lock className="w-3 h-3" /> Privado</>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-surface-300">
                      {Object.keys(template.operations || {}).length}
                    </td>
                    <td className="px-4 py-3 text-surface-300">
                      {template._count?.instances || 0}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(template)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDocumentation(template)} title="Documentação">
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleViewJson(template)}>
                          <Code className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(template)} className="text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}


      {/* ============================================ */}
      {/* DETAILS MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={selectedTemplate?.name || 'Detalhes do Template'}
        size="xl"
      >
        {selectedTemplate && (
          <div className="space-y-6">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-surface-700 pb-2">
              {[
                { key: 'overview', label: 'Visão Geral', icon: Eye },
                { key: 'auth', label: 'Autenticação', icon: Key },
                { key: 'operations', label: 'Operações', icon: Activity },
                { key: 'webhooks', label: 'Webhooks', icon: Webhook },
                { key: 'credentials', label: 'Credenciais', icon: Lock },
                { key: 'blockchain', label: 'Blockchain', icon: Layers },
                { key: 'instances', label: 'Instâncias', icon: Building2 },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as typeof activeTab)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === key
                      ? 'bg-primary-500 text-white'
                      : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Nome</label>
                        <p className="text-surface-100 font-medium">{selectedTemplate.name}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Versão</label>
                        <p className="text-surface-100 font-medium">v{selectedTemplate.version}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Categoria</label>
                        <Badge className={`bg-gradient-to-r ${getCategoryInfo(selectedTemplate.category).gradient} text-white`}>
                          {getCategoryInfo(selectedTemplate.category).emoji} {getCategoryInfo(selectedTemplate.category).label}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Tipo</label>
                        <p className={`${getTypeInfo(selectedTemplate.type).color}`}>
                          {getTypeInfo(selectedTemplate.type).label}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Status</label>
                        <div className="flex items-center gap-2">
                          <Badge className={`${getStatusInfo(selectedTemplate.status).bgColor} ${getStatusInfo(selectedTemplate.status).color}`}>
                            {getStatusInfo(selectedTemplate.status).emoji} {getStatusInfo(selectedTemplate.status).label}
                          </Badge>
                          <Select
                            value={selectedTemplate.status}
                            onChange={(e) => handleChangeStatus(selectedTemplate, e.target.value)}
                            options={STATUS_OPTIONS.filter(o => o.value)}
                            className="w-40"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Base URL</label>
                        <p className="text-surface-100 font-mono text-sm">{selectedTemplate.baseUrl || 'N/A'}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Criado em</label>
                        <p className="text-surface-100">{formatDate(selectedTemplate.createdAt)}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Atualizado em</label>
                        <p className="text-surface-100">{formatDate(selectedTemplate.updatedAt)}</p>
                      </div>
                    </div>
                    {selectedTemplate.description && (
                      <div className="space-y-2">
                        <label className="text-sm text-surface-400">Descrição</label>
                        <p className="text-surface-300">{selectedTemplate.description}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-4 pt-4 border-t border-surface-700">
                      <div className="flex items-center gap-2">
                        <Download className="w-4 h-4 text-surface-400" />
                        <span className="text-surface-300">{selectedTemplate.downloads} downloads</span>
                      </div>
                      {selectedTemplate.rating && renderStars(selectedTemplate.rating)}
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-surface-400" />
                        <span className="text-surface-300">{selectedTemplate._count?.instances || 0} instâncias</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* AUTH TAB */}
                {activeTab === 'auth' && (
                  <div className="space-y-4">
                    <Card className="p-4 bg-surface-800/50">
                      <div className="flex items-center gap-3 mb-4">
                        {(() => {
                          const authInfo = getAuthInfo(selectedTemplate.authConfig?.type);
                          const AuthIcon = authInfo.icon;
                          return (
                            <>
                              <div className={`w-10 h-10 rounded-xl bg-surface-700 flex items-center justify-center ${authInfo.color}`}>
                                <AuthIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-semibold text-surface-100">{authInfo.label}</p>
                                <p className="text-sm text-surface-400">{authInfo.description}</p>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(selectedTemplate.authConfig || {}).map(([key, value]) => (
                          <div key={key} className="space-y-1">
                            <label className="text-xs text-surface-500 uppercase">{key}</label>
                            <p className="text-surface-200 font-mono text-sm bg-surface-900 px-2 py-1 rounded">
                              {String(value) || 'N/A'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                )}

                {/* OPERATIONS TAB */}
                {activeTab === 'operations' && (
                  <div className="space-y-3">
                    {Object.entries(selectedTemplate.operations || {}).length === 0 ? (
                      <p className="text-surface-400 text-center py-8">Nenhuma operação configurada</p>
                    ) : (
                      Object.entries(selectedTemplate.operations).map(([opName, op]) => (
                        <Card key={opName} className="overflow-hidden">
                          <button
                            onClick={() => toggleOperation(opName)}
                            className="w-full p-4 flex items-center justify-between hover:bg-surface-800/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className={`px-2 py-1 rounded text-xs font-bold border ${getMethodColor(op.method)}`}>
                                {op.method}
                              </span>
                              <span className="font-medium text-surface-100">{opName}</span>
                              <span className="text-surface-400 font-mono text-sm">{op.path}</span>
                            </div>
                            {expandedOperations.has(opName) ? (
                              <ChevronDown className="w-5 h-5 text-surface-400" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-surface-400" />
                            )}
                          </button>
                          <AnimatePresence>
                            {expandedOperations.has(opName) && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-surface-700"
                              >
                                <div className="p-4 space-y-4 bg-surface-800/30">
                                  {op.description && (
                                    <p className="text-surface-300">{op.description}</p>
                                  )}
                                  {op.timeout && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <Clock className="w-4 h-4 text-surface-400" />
                                      <span className="text-surface-400">Timeout:</span>
                                      <span className="text-surface-200">{op.timeout}ms</span>
                                    </div>
                                  )}
                                  {op.retryConfig && (
                                    <div className="flex items-center gap-2 text-sm">
                                      <RefreshCw className="w-4 h-4 text-surface-400" />
                                      <span className="text-surface-400">Retry:</span>
                                      <span className="text-surface-200">{op.retryConfig.maxRetries}x com {op.retryConfig.backoffMs}ms backoff</span>
                                    </div>
                                  )}
                                  {op.requestMapping && Object.keys(op.requestMapping).length > 0 && (
                                    <div>
                                      <p className="text-sm text-surface-400 mb-2">Request Mapping:</p>
                                      <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                                        {JSON.stringify(op.requestMapping, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {op.responseMapping && Object.keys(op.responseMapping).length > 0 && (
                                    <div>
                                      <p className="text-sm text-surface-400 mb-2">Response Mapping:</p>
                                      <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                                        {JSON.stringify(op.responseMapping, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Card>
                      ))
                    )}
                  </div>
                )}

                {/* WEBHOOKS TAB */}
                {activeTab === 'webhooks' && (
                  <div className="space-y-4">
                    {selectedTemplate.webhookConfig ? (
                      <>
                        <Card className="p-4 bg-surface-800/50">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                              <Webhook className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                              <p className="font-semibold text-surface-100">Configuração de Webhooks</p>
                              <p className="text-sm text-surface-400">Eventos que este template pode receber</p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            {selectedTemplate.webhookConfig.signatureHeader && (
                              <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-surface-400" />
                                <span className="text-surface-400">Header de Assinatura:</span>
                                <code className="text-surface-200 bg-surface-900 px-2 py-1 rounded text-sm">
                                  {selectedTemplate.webhookConfig.signatureHeader}
                                </code>
                              </div>
                            )}
                            {selectedTemplate.webhookConfig.signatureAlgorithm && (
                              <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-surface-400" />
                                <span className="text-surface-400">Algoritmo:</span>
                                <code className="text-surface-200 bg-surface-900 px-2 py-1 rounded text-sm">
                                  {selectedTemplate.webhookConfig.signatureAlgorithm}
                                </code>
                              </div>
                            )}
                          </div>
                        </Card>
                        <div>
                          <p className="text-sm text-surface-400 mb-2">Eventos Suportados:</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedTemplate.webhookConfig.events?.map((event) => (
                              <Badge key={event} variant="default">
                                {event}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {selectedTemplate.webhookConfig.eventMapping && (
                          <div>
                            <p className="text-sm text-surface-400 mb-2">Mapeamento de Eventos:</p>
                            <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                              {JSON.stringify(selectedTemplate.webhookConfig.eventMapping, null, 2)}
                            </pre>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <Webhook className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                        <p className="text-surface-400">Nenhum webhook configurado</p>
                      </div>
                    )}
                  </div>
                )}

                {/* CREDENTIALS TAB */}
                {activeTab === 'credentials' && (
                  <div className="space-y-4">
                    <Card className="p-4 bg-surface-800/50">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                          <Key className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-surface-100">Schema de Credenciais</p>
                          <p className="text-sm text-surface-400">Campos necessários para configurar uma instância</p>
                        </div>
                      </div>
                      {selectedTemplate.credentialSchema?.properties ? (
                        <div className="space-y-3">
                          {Object.entries(selectedTemplate.credentialSchema.properties).map(([fieldName, field]) => (
                            <div key={fieldName} className="p-3 bg-surface-900 rounded-lg">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-surface-100">{field.title || fieldName}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="default" size="sm">{field.type}</Badge>
                                  {selectedTemplate.credentialSchema.required?.includes(fieldName) && (
                                    <Badge variant="error" size="sm">Obrigatório</Badge>
                                  )}
                                </div>
                              </div>
                              {field.description && (
                                <p className="text-sm text-surface-400">{field.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-surface-400">Nenhum schema definido</p>
                      )}
                    </Card>
                  </div>
                )}

                {/* BLOCKCHAIN TAB */}
                {activeTab === 'blockchain' && (
                  <div className="space-y-4">
                    {selectedTemplate.chainConfig || selectedTemplate.contractConfig || selectedTemplate.dexConfig ? (
                      <>
                        {selectedTemplate.chainConfig && (
                          <Card className="p-4 bg-surface-800/50">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                                <Layers className="w-5 h-5 text-purple-400" />
                              </div>
                              <div>
                                <p className="font-semibold text-surface-100">Chain Config</p>
                                <p className="text-sm text-surface-400">Configuração de blockchain</p>
                              </div>
                            </div>
                            <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                              {JSON.stringify(selectedTemplate.chainConfig, null, 2)}
                            </pre>
                          </Card>
                        )}
                        {selectedTemplate.contractConfig && (
                          <Card className="p-4 bg-surface-800/50">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                                <Code className="w-5 h-5 text-cyan-400" />
                              </div>
                              <div>
                                <p className="font-semibold text-surface-100">Contract Config</p>
                                <p className="text-sm text-surface-400">Configuração de smart contracts</p>
                              </div>
                            </div>
                            <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                              {JSON.stringify(selectedTemplate.contractConfig, null, 2)}
                            </pre>
                          </Card>
                        )}
                        {selectedTemplate.dexConfig && (
                          <Card className="p-4 bg-surface-800/50">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                                <Activity className="w-5 h-5 text-orange-400" />
                              </div>
                              <div>
                                <p className="font-semibold text-surface-100">DEX Config</p>
                                <p className="text-sm text-surface-400">Configuração de exchange descentralizada</p>
                              </div>
                            </div>
                            <pre className="bg-surface-900 p-3 rounded text-xs text-surface-300 overflow-x-auto">
                              {JSON.stringify(selectedTemplate.dexConfig, null, 2)}
                            </pre>
                          </Card>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <Layers className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                        <p className="text-surface-400">Nenhuma configuração blockchain</p>
                        <p className="text-sm text-surface-500">Este template não é do tipo blockchain</p>
                      </div>
                    )}
                  </div>
                )}

                {/* INSTANCES TAB */}
                {activeTab === 'instances' && (
                  <div className="space-y-4">
                    {loadingInstances ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 text-primary-500 animate-spin" />
                      </div>
                    ) : templateInstances.length === 0 ? (
                      <div className="text-center py-8">
                        <Building2 className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                        <p className="text-surface-400">Nenhuma instância usando este template</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {templateInstances.map((instance) => (
                          <Card key={instance.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-surface-700 flex items-center justify-center">
                                  <Building2 className="w-5 h-5 text-surface-400" />
                                </div>
                                <div>
                                  <p className="font-medium text-surface-100">{instance.name}</p>
                                  <p className="text-sm text-surface-400">{instance.tenant?.name}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <Badge variant={instance.status === 'ACTIVE' ? 'success' : 'default'}>
                                  {instance.status}
                                </Badge>
                                <div className="text-right">
                                  <p className="text-sm text-surface-300">{instance.requestCount || 0} requests</p>
                                  <p className="text-xs text-surface-500">
                                    {instance.lastUsedAt ? `Último uso: ${formatDate(instance.lastUsedAt)}` : 'Nunca usado'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-surface-700">
              <Button variant="secondary" onClick={() => handleViewJson(selectedTemplate)}>
                <Code className="w-4 h-4 mr-2" />
                Ver JSON
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={() => handleClone(selectedTemplate)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Clonar
                </Button>
                <Button variant="primary" onClick={() => handleEdit(selectedTemplate)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>


      {/* ============================================ */}
      {/* JSON MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showJsonModal}
        onClose={() => setShowJsonModal(false)}
        title="Template JSON"
        size="lg"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-surface-400">Exportar ou copiar a configuração completa do template</p>
              <Button variant="secondary" size="sm" onClick={() => copyToClipboard(JSON.stringify(selectedTemplate, null, 2))}>
                <Copy className="w-4 h-4 mr-2" />
                Copiar
              </Button>
            </div>
            <pre className="bg-surface-900 p-4 rounded-lg text-xs text-surface-300 overflow-auto max-h-[500px]">
              {JSON.stringify(selectedTemplate, null, 2)}
            </pre>
            <div className="flex justify-end">
              <Button variant="primary" onClick={() => {
                const blob = new Blob([JSON.stringify(selectedTemplate, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${selectedTemplate.name.toLowerCase().replace(/\s+/g, '-')}-template.json`;
                a.click();
              }}>
                <Download className="w-4 h-4 mr-2" />
                Download JSON
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ============================================ */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirmar Exclusão"
        size="sm"
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />
              <div>
                <p className="font-semibold text-surface-100">Tem certeza?</p>
                <p className="text-sm text-surface-400">
                  Esta ação não pode ser desfeita. O template "{selectedTemplate.name}" será permanentemente excluído.
                </p>
              </div>
            </div>
            {selectedTemplate._count?.instances ? (
              <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <Info className="w-5 h-5 text-yellow-400" />
                <p className="text-sm text-yellow-200">
                  Este template possui {selectedTemplate._count.instances} instância(s) ativa(s). Elas serão desvinculadas.
                </p>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleConfirmDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir Template
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ============================================ */}
      {/* HELP MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Entendendo Templates vs Instâncias"
        size="lg"
      >
        <div className="space-y-6">
          {/* Intro */}
          <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary-500/10 to-accent-500/10 rounded-xl border border-primary-500/20">
            <Lightbulb className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-surface-100 mb-2">O que são Templates?</h3>
              <p className="text-surface-300">
                Templates são <strong className="text-primary-400">blueprints</strong> (receitas) que definem como conectar a um provider externo. 
                Eles contêm toda a configuração técnica: endpoints, autenticação, mapeamento de dados, webhooks, etc.
              </p>
            </div>
          </div>

          {/* Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4 border-primary-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                  <Puzzle className="w-6 h-6 text-primary-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-surface-100">Template</h4>
                  <p className="text-sm text-surface-400">Blueprint / Receita</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-surface-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Define COMO conectar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Configuração técnica
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Reutilizável por vários tenants
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Gerenciado pelo Super Admin
                </li>
              </ul>
              <div className="mt-4 p-3 bg-surface-800 rounded-lg">
                <p className="text-xs text-surface-400">Exemplo:</p>
                <p className="text-sm text-surface-200">"Template OpenPix" - define endpoints, auth, webhooks</p>
              </div>
            </Card>

            <Card className="p-4 border-accent-500/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center">
                  <Play className="w-6 h-6 text-accent-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-surface-100">Instância</h4>
                  <p className="text-sm text-surface-400">Conexão Ativa</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-surface-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  USA o template
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Credenciais específicas
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Pertence a um tenant
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Gerenciado pelo Tenant Admin
                </li>
              </ul>
              <div className="mt-4 p-3 bg-surface-800 rounded-lg">
                <p className="text-xs text-surface-400">Exemplo:</p>
                <p className="text-sm text-surface-200">"OpenPix da Empresa X" - com API key própria</p>
              </div>
            </Card>
          </div>

          {/* Flow */}
          <Card className="p-4 bg-surface-800/50">
            <h4 className="font-semibold text-surface-100 mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-primary-400" />
              Fluxo de Uso
            </h4>
            <div className="flex items-center justify-between">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">1️⃣</span>
                </div>
                <p className="text-sm text-surface-300">Super Admin</p>
                <p className="text-xs text-surface-500">Cria Template</p>
              </div>
              <ChevronRight className="w-6 h-6 text-surface-600" />
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">2️⃣</span>
                </div>
                <p className="text-sm text-surface-300">Publica</p>
                <p className="text-xs text-surface-500">Template disponível</p>
              </div>
              <ChevronRight className="w-6 h-6 text-surface-600" />
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">3️⃣</span>
                </div>
                <p className="text-sm text-surface-300">Tenant Admin</p>
                <p className="text-xs text-surface-500">Cria Instância</p>
              </div>
              <ChevronRight className="w-6 h-6 text-surface-600" />
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-accent-500/20 flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">4️⃣</span>
                </div>
                <p className="text-sm text-surface-300">Usa</p>
                <p className="text-xs text-surface-500">Transações reais</p>
              </div>
            </div>
          </Card>

          {/* Categories */}
          <div>
            <h4 className="font-semibold text-surface-100 mb-3">Categorias de Templates</h4>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                <Card key={key} className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{info.emoji}</span>
                    <span className="font-medium text-surface-100">{info.label}</span>
                  </div>
                  <p className="text-xs text-surface-400">{info.description}</p>
                </Card>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setShowHelpModal(false)}>
              Entendi!
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* EDIT MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        title={`Editar Template: ${selectedTemplate?.name || ''}`}
        size="xl"
      >
        {editFormData && (
          <div className="space-y-6">
            {/* Error Banner */}
            {editErrors._general && (
              <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-200">{editErrors._general}</p>
              </div>
            )}

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-surface-700 pb-2">
              {[
                { key: 'basic', label: 'Informações', icon: Info },
                { key: 'auth', label: 'Autenticação', icon: Key },
                { key: 'operations', label: 'Operações', icon: Activity },
                { key: 'webhooks', label: 'Webhooks', icon: Webhook },
                { key: 'credentials', label: 'Credenciais', icon: Lock },
                { key: 'blockchain', label: 'Blockchain', icon: Layers, hidden: editFormData.category !== 'CRYPTO' },
                { key: 'preview', label: 'Preview', icon: Eye },
              ].filter(tab => !tab.hidden).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setEditActiveTab(key as typeof editActiveTab)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    editActiveTab === key
                      ? 'bg-primary-500 text-white'
                      : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={editActiveTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="min-h-[400px]"
              >
                {/* BASIC INFO TAB */}
                {editActiveTab === 'basic' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Nome <span className="text-red-400">*</span>
                        </label>
                        <Input
                          value={editFormData.name}
                          onChange={(e) => handleEditFormChange('name', e.target.value)}
                          placeholder="Nome do template"
                          error={editErrors.name}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Versão <span className="text-red-400">*</span>
                        </label>
                        <Input
                          value={editFormData.version}
                          onChange={(e) => handleEditFormChange('version', e.target.value)}
                          placeholder="1.0.0"
                          error={editErrors.version}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Categoria <span className="text-red-400">*</span>
                        </label>
                        <Select
                          value={editFormData.category}
                          onChange={(e) => handleEditFormChange('category', e.target.value)}
                          options={CATEGORY_OPTIONS.filter(o => o.value)}
                          error={editErrors.category}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Tipo <span className="text-red-400">*</span>
                        </label>
                        <Select
                          value={editFormData.type}
                          onChange={(e) => handleEditFormChange('type', e.target.value)}
                          options={TYPE_OPTIONS.filter(o => o.value)}
                          error={editErrors.type}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Status
                        </label>
                        <Select
                          value={editFormData.status}
                          onChange={(e) => handleEditFormChange('status', e.target.value)}
                          options={STATUS_OPTIONS.filter(o => o.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-surface-300 mb-1">
                          Base URL
                        </label>
                        <Input
                          value={editFormData.baseUrl || ''}
                          onChange={(e) => handleEditFormChange('baseUrl', e.target.value)}
                          placeholder="https://api.provider.com"
                          error={editErrors.baseUrl}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-1">
                        Descrição
                      </label>
                      <textarea
                        value={editFormData.description || ''}
                        onChange={(e) => handleEditFormChange('description', e.target.value)}
                        placeholder="Descrição do template..."
                        className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        rows={3}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isPublic"
                        checked={editFormData.isPublic}
                        onChange={(e) => handleEditFormChange('isPublic', e.target.checked)}
                        className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
                      />
                      <label htmlFor="isPublic" className="text-sm text-surface-300">
                        Template público (visível para todos os tenants)
                      </label>
                    </div>
                  </div>
                )}

                {/* AUTH CONFIG TAB */}
                {editActiveTab === 'auth' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-surface-300 mb-3">
                        Tipo de Autenticação
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {Object.entries(AUTH_TYPE_INFO).map(([key, info]) => {
                          const AuthIcon = info.icon;
                          const isSelected = editFormData.authConfig?.type === key;
                          return (
                            <button
                              key={key}
                              onClick={() => handleEditFormChange('authConfig.type', key)}
                              className={`p-4 rounded-lg border-2 transition-all text-left ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-500/10'
                                  : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
                              }`}
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <AuthIcon className={`w-5 h-5 ${info.color}`} />
                                <span className="font-medium text-surface-100">{info.label}</span>
                              </div>
                              <p className="text-xs text-surface-400">{info.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Dynamic fields based on auth type */}
                    {editFormData.authConfig?.type === 'API_KEY' && (
                      <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Header Name
                          </label>
                          <Input
                            value={editFormData.authConfig.headerName || ''}
                            onChange={(e) => handleEditFormChange('authConfig.headerName', e.target.value)}
                            placeholder="Authorization"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Prefix
                          </label>
                          <Input
                            value={editFormData.authConfig.prefix || ''}
                            onChange={(e) => handleEditFormChange('authConfig.prefix', e.target.value)}
                            placeholder="Bearer"
                          />
                        </div>
                      </div>
                    )}

                    {editFormData.authConfig?.type === 'BASIC_AUTH' && (
                      <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Username Field
                          </label>
                          <Input
                            value={editFormData.authConfig.usernameField || ''}
                            onChange={(e) => handleEditFormChange('authConfig.usernameField', e.target.value)}
                            placeholder="username"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Password Field
                          </label>
                          <Input
                            value={editFormData.authConfig.passwordField || ''}
                            onChange={(e) => handleEditFormChange('authConfig.passwordField', e.target.value)}
                            placeholder="password"
                          />
                        </div>
                      </div>
                    )}

                    {editFormData.authConfig?.type === 'OAUTH2' && (
                      <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Token URL
                          </label>
                          <Input
                            value={editFormData.authConfig.tokenUrl || ''}
                            onChange={(e) => handleEditFormChange('authConfig.tokenUrl', e.target.value)}
                            placeholder="https://auth.provider.com/oauth/token"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Grant Type
                          </label>
                          <Select
                            value={editFormData.authConfig.grantType || ''}
                            onChange={(e) => handleEditFormChange('authConfig.grantType', e.target.value)}
                            options={[
                              { value: 'client_credentials', label: 'Client Credentials' },
                              { value: 'authorization_code', label: 'Authorization Code' },
                              { value: 'refresh_token', label: 'Refresh Token' },
                            ]}
                          />
                        </div>
                      </div>
                    )}

                    {editFormData.authConfig?.type === 'HMAC_SIGNATURE' && (
                      <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Signature Param
                          </label>
                          <Input
                            value={editFormData.authConfig.signatureParam || ''}
                            onChange={(e) => handleEditFormChange('authConfig.signatureParam', e.target.value)}
                            placeholder="X-Signature"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Timestamp Param
                          </label>
                          <Input
                            value={editFormData.authConfig.timestampParam || ''}
                            onChange={(e) => handleEditFormChange('authConfig.timestampParam', e.target.value)}
                            placeholder="X-Timestamp"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* OPERATIONS TAB */}
                {editActiveTab === 'operations' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-surface-400">
                        {Object.keys(editFormData.operations || {}).length} operações configuradas
                      </p>
                      <Button variant="primary" size="sm" onClick={handleAddOperation}>
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Operação
                      </Button>
                    </div>
                    {Object.entries(editFormData.operations || {}).length === 0 ? (
                      <div className="text-center py-8">
                        <Activity className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                        <p className="text-surface-400">Nenhuma operação configurada</p>
                        <p className="text-sm text-surface-500 mt-1">Clique em "Adicionar Operação" para começar</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(editFormData.operations).map(([opName, op]) => (
                          <Card key={opName} className="p-4 hover:border-surface-600 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 rounded text-xs font-bold border ${getMethodColor(op.method)}`}>
                                  {op.method}
                                </span>
                                <span className="font-medium text-surface-100">{opName}</span>
                                <span className="text-surface-400 font-mono text-sm">{op.path}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => handleEditOperation(opName)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-400 hover:text-red-300"
                                  onClick={() => handleDeleteOperation(opName)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            {op.description && (
                              <p className="text-sm text-surface-400 mt-2">{op.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
                              {op.timeout && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {op.timeout}ms
                                </span>
                              )}
                              {op.retryConfig && (
                                <span className="flex items-center gap-1">
                                  <RefreshCw className="w-3 h-3" />
                                  {op.retryConfig.maxRetries}x retry
                                </span>
                              )}
                              {op.requestMapping && Object.keys(op.requestMapping).length > 0 && (
                                <Badge variant="default" size="sm">Request Mapping</Badge>
                              )}
                              {op.responseMapping && Object.keys(op.responseMapping).length > 0 && (
                                <Badge variant="default" size="sm">Response Mapping</Badge>
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* WEBHOOKS TAB */}
                {editActiveTab === 'webhooks' && (
                  <div className="space-y-4">
                    <Card className="p-4 bg-surface-800/50">
                      <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-400" />
                        Configuração de Assinatura
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Signature Header
                          </label>
                          <Input
                            value={editFormData.webhookConfig?.signatureHeader || ''}
                            onChange={(e) => {
                              const newConfig = { ...editFormData.webhookConfig, signatureHeader: e.target.value };
                              handleEditFormChange('webhookConfig', newConfig);
                            }}
                            placeholder="X-Webhook-Signature"
                            leftIcon={<Key className="w-4 h-4" />}
                          />
                          <p className="text-xs text-surface-500 mt-1">
                            Header HTTP que contém a assinatura do webhook
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Signature Algorithm
                          </label>
                          <Select
                            value={editFormData.webhookConfig?.signatureAlgorithm || ''}
                            onChange={(e) => {
                              const newConfig = { ...editFormData.webhookConfig, signatureAlgorithm: e.target.value };
                              handleEditFormChange('webhookConfig', newConfig);
                            }}
                            options={[
                              { value: '', label: 'Selecione...' },
                              { value: 'sha256', label: 'HMAC-SHA256 (Recomendado)' },
                              { value: 'sha512', label: 'HMAC-SHA512' },
                              { value: 'sha1', label: 'HMAC-SHA1' },
                              { value: 'md5', label: 'MD5 (Não recomendado)' },
                            ]}
                          />
                          <p className="text-xs text-surface-500 mt-1">
                            Algoritmo usado para validar a assinatura
                          </p>
                        </div>
                      </div>
                    </Card>
                    
                    <Card className="p-4 bg-surface-800/50">
                      <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
                        <Webhook className="w-4 h-4 text-purple-400" />
                        Eventos Suportados
                      </h4>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {editFormData.webhookConfig?.events?.length ? (
                          editFormData.webhookConfig.events.map((event, idx) => (
                            <Badge key={idx} variant="primary" className="flex items-center gap-1 px-3 py-1">
                              {event}
                              <button
                                onClick={() => {
                                  const newEvents = editFormData.webhookConfig?.events?.filter((_, i) => i !== idx) || [];
                                  const newConfig = { ...editFormData.webhookConfig, events: newEvents };
                                  handleEditFormChange('webhookConfig', newConfig);
                                }}
                                className="ml-1 hover:text-red-400 transition-colors"
                              >
                                ×
                              </button>
                            </Badge>
                          ))
                        ) : (
                          <p className="text-surface-500 text-sm">Nenhum evento configurado</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          value={newWebhookEvent}
                          onChange={(e) => setNewWebhookEvent(e.target.value)}
                          placeholder="PAYMENT_RECEIVED, CHARGE_COMPLETED..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddWebhookEvent();
                            }
                          }}
                        />
                        <Button variant="secondary" onClick={handleAddWebhookEvent}>
                          <Plus className="w-4 h-4 mr-1" />
                          Adicionar
                        </Button>
                      </div>
                      <p className="text-xs text-surface-500 mt-2">
                        Pressione Enter ou clique em Adicionar para incluir um novo evento
                      </p>
                    </Card>

                    {/* Event Mapping */}
                    <Card className="p-4 bg-surface-800/50">
                      <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
                        <GitBranch className="w-4 h-4 text-green-400" />
                        Mapeamento de Eventos (JSON)
                      </h4>
                      <textarea
                        value={JSON.stringify(editFormData.webhookConfig?.eventMapping || {}, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            const newConfig = { ...editFormData.webhookConfig, eventMapping: parsed };
                            handleEditFormChange('webhookConfig', newConfig);
                          } catch {
                            // Invalid JSON, don't update
                          }
                        }}
                        className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        rows={6}
                        placeholder='{"PROVIDER_EVENT": "INTERNAL_EVENT"}'
                      />
                      <p className="text-xs text-surface-500 mt-2">
                        Mapeia eventos do provider para eventos internos do sistema
                      </p>
                    </Card>
                  </div>
                )}

                {/* CREDENTIALS TAB */}
                {editActiveTab === 'credentials' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-surface-400">
                        Schema de credenciais define quais campos o tenant precisa preencher ao criar uma instância.
                      </p>
                      <Button variant="primary" size="sm" onClick={handleAddCredentialField}>
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Campo
                      </Button>
                    </div>
                    
                    {editFormData.credentialSchema?.properties && Object.keys(editFormData.credentialSchema.properties).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(editFormData.credentialSchema.properties).map(([fieldName, field]) => (
                          <Card key={fieldName} className="p-4 hover:border-primary-500/30 transition-all">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-surface-100">{field.title || fieldName}</span>
                                  <code className="text-xs text-surface-500 bg-surface-800 px-2 py-0.5 rounded">
                                    {fieldName}
                                  </code>
                                </div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="default" size="sm">{field.type}</Badge>
                                  {field.format && (
                                    <Badge variant="info" size="sm">{field.format}</Badge>
                                  )}
                                  {editFormData.credentialSchema.required?.includes(fieldName) ? (
                                    <Badge variant="error" size="sm">Obrigatório</Badge>
                                  ) : (
                                    <Badge variant="default" size="sm">Opcional</Badge>
                                  )}
                                </div>
                                {field.description && (
                                  <p className="text-sm text-surface-400">{field.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 ml-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditCredentialField(fieldName)}
                                  className="text-primary-400 hover:text-primary-300"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteCredentialField(fieldName)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card className="p-8 text-center border-dashed">
                        <Key className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                        <p className="text-surface-400 mb-4">Nenhum campo de credencial definido</p>
                        <Button variant="secondary" size="sm" onClick={handleAddCredentialField}>
                          <Plus className="w-4 h-4 mr-1" />
                          Adicionar Primeiro Campo
                        </Button>
                      </Card>
                    )}

                    {/* JSON Schema Preview */}
                    {editFormData.credentialSchema?.properties && Object.keys(editFormData.credentialSchema.properties).length > 0 && (
                      <Card className="p-4 bg-surface-800/50">
                        <div className="flex items-center gap-2 mb-3">
                          <Code className="w-4 h-4 text-primary-400" />
                          <span className="text-sm font-medium text-surface-200">JSON Schema Preview</span>
                        </div>
                        <pre className="text-xs text-surface-400 bg-surface-900 p-3 rounded-lg overflow-x-auto">
                          {JSON.stringify(editFormData.credentialSchema, null, 2)}
                        </pre>
                      </Card>
                    )}
                  </div>
                )}

                {/* BLOCKCHAIN TAB */}
                {editActiveTab === 'blockchain' && editFormData.category === 'CRYPTO' && (
                  <div className="space-y-4">
                    <Card className="p-4 bg-surface-800/50">
                      <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-orange-400" />
                        DEX Config
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Router Address
                          </label>
                          <Input
                            value={editFormData.dexConfig?.routerAddress || ''}
                            onChange={(e) => {
                              const newConfig = { ...editFormData.dexConfig, routerAddress: e.target.value };
                              handleEditFormChange('dexConfig', newConfig);
                            }}
                            placeholder="0x..."
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-surface-300 mb-1">
                            Factory Address
                          </label>
                          <Input
                            value={editFormData.dexConfig?.factoryAddress || ''}
                            onChange={(e) => {
                              const newConfig = { ...editFormData.dexConfig, factoryAddress: e.target.value };
                              handleEditFormChange('dexConfig', newConfig);
                            }}
                            placeholder="0x..."
                          />
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4 bg-surface-800/50">
                      <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
                        <Layers className="w-4 h-4 text-purple-400" />
                        Chain Config (JSON)
                      </h4>
                      <textarea
                        value={JSON.stringify(editFormData.chainConfig || {}, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            handleEditFormChange('chainConfig', parsed);
                          } catch {
                            // Invalid JSON, don't update
                          }
                        }}
                        className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                        rows={6}
                      />
                    </Card>
                  </div>
                )}

                {/* PREVIEW TAB */}
                {editActiveTab === 'preview' && (
                  <div className="space-y-4">
                    <p className="text-surface-400 text-sm">
                      Visualização de como o template aparecerá no catálogo para Tenant Admins.
                    </p>
                    <Card className="p-4 hover:border-primary-500/50 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getCategoryInfo(editFormData.category).gradient} flex items-center justify-center`}>
                            <span className="text-lg">{getCategoryInfo(editFormData.category).emoji}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-surface-100">
                              {editFormData.name || 'Nome do Template'}
                            </h3>
                            <p className="text-xs text-surface-500">v{editFormData.version || '1.0.0'}</p>
                          </div>
                        </div>
                        <Badge className={`${getStatusInfo(editFormData.status).bgColor} ${getStatusInfo(editFormData.status).color}`}>
                          {getStatusInfo(editFormData.status).emoji} {getStatusInfo(editFormData.status).label}
                        </Badge>
                      </div>
                      <p className="text-sm text-surface-400 mb-3 line-clamp-2">
                        {editFormData.description || `Template ${getTypeInfo(editFormData.type).label} para ${getCategoryInfo(editFormData.category).label}`}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="default" size="sm" className={getTypeInfo(editFormData.type).color}>
                          {getTypeInfo(editFormData.type).label}
                        </Badge>
                        <Badge variant="default" size="sm">
                          {Object.keys(editFormData.operations || {}).length} operações
                        </Badge>
                        <Badge variant="default" size="sm">
                          {getAuthInfo(editFormData.authConfig?.type).label}
                        </Badge>
                      </div>
                    </Card>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Dirty indicator */}
            {editIsDirty && (
              <div className="flex items-center gap-2 text-yellow-400 text-sm">
                <AlertTriangle className="w-4 h-4" />
                Alterações não salvas
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-surface-700">
              <div className="text-xs text-surface-500">
                Ctrl+S para salvar • Esc para fechar
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={handleCloseEditModal}>
                  Cancelar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSaveEdit}
                  disabled={editIsSaving || !editIsDirty}
                >
                  {editIsSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Salvar Alterações
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ============================================ */}
      {/* UNSAVED CHANGES CONFIRMATION MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showUnsavedChangesModal}
        onClose={() => setShowUnsavedChangesModal(false)}
        title="Alterações não salvas"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
            <div>
              <p className="font-semibold text-surface-100">Você tem alterações não salvas</p>
              <p className="text-sm text-surface-400">
                Deseja descartar as alterações e fechar o editor?
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowUnsavedChangesModal(false)}>
              Continuar Editando
            </Button>
            <Button variant="danger" onClick={handleConfirmCloseEdit}>
              Descartar Alterações
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* OPERATION EDITOR MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showOperationEditor}
        onClose={() => setShowOperationEditor(false)}
        title={editingOperationName ? `Editar Operação: ${editingOperationName}` : 'Nova Operação'}
        size="lg"
      >
        <div className="space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between mb-6">
            {[
              { step: 1, label: 'Identificação', icon: Info },
              { step: 2, label: 'Endpoint', icon: Globe },
              { step: 3, label: 'Mapeamento', icon: GitBranch },
              { step: 4, label: 'Configuração', icon: Clock },
            ].map(({ step, label, icon: Icon }, idx) => (
              <div key={step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      operationEditorStep === step
                        ? 'bg-primary-500 text-white'
                        : operationEditorStep > step
                        ? 'bg-green-500 text-white'
                        : 'bg-surface-700 text-surface-400'
                    }`}
                  >
                    {operationEditorStep > step ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${operationEditorStep === step ? 'text-primary-400' : 'text-surface-500'}`}>
                    {label}
                  </span>
                </div>
                {idx < 3 && (
                  <div className={`w-16 h-0.5 mx-2 ${operationEditorStep > step ? 'bg-green-500' : 'bg-surface-700'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={operationEditorStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="min-h-[300px]"
            >
              {/* STEP 1: Identification */}
              {operationEditorStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Nome da Operação <span className="text-red-400">*</span>
                    </label>
                    <Input
                      value={operationFormData.name}
                      onChange={(e) => handleOperationFormChange('name', e.target.value)}
                      placeholder="createCharge, getBalance, sendPix..."
                      error={operationErrors.name}
                    />
                    <p className="text-xs text-surface-500 mt-1">
                      Use camelCase. Ex: createCharge, getBalance, listTransactions
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Método HTTP <span className="text-red-400">*</span>
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
                        <button
                          key={method}
                          onClick={() => handleOperationFormChange('method', method)}
                          className={`p-3 rounded-lg border-2 transition-all font-bold text-sm ${
                            operationFormData.method === method
                              ? `${getMethodColor(method)} border-current`
                              : 'border-surface-700 text-surface-400 hover:border-surface-600'
                          }`}
                        >
                          {method}
                        </button>
                      ))}
                    </div>
                    {operationErrors.method && (
                      <p className="text-red-400 text-sm mt-1">{operationErrors.method}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Descrição
                    </label>
                    <textarea
                      value={operationFormData.description}
                      onChange={(e) => handleOperationFormChange('description', e.target.value)}
                      placeholder="Descreva o que esta operação faz..."
                      className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* STEP 2: Endpoint */}
              {operationEditorStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Path do Endpoint <span className="text-red-400">*</span>
                    </label>
                    <Input
                      value={operationFormData.path}
                      onChange={(e) => handleOperationFormChange('path', e.target.value)}
                      placeholder="/v1/charges, /api/pix/{id}, /balance"
                      error={operationErrors.path}
                      leftIcon={<Code className="w-4 h-4" />}
                    />
                    <p className="text-xs text-surface-500 mt-1">
                      Use {'{param}'} para parâmetros dinâmicos. Ex: /charges/{'{id}'}/refund
                    </p>
                  </div>
                  
                  <Card className="p-4 bg-surface-800/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-medium text-surface-200">Preview da URL</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-surface-900 rounded-lg font-mono text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getMethodColor(operationFormData.method)}`}>
                        {operationFormData.method}
                      </span>
                      <span className="text-surface-400">{editFormData?.baseUrl || 'https://api.provider.com'}</span>
                      <span className="text-primary-400">{operationFormData.path}</span>
                    </div>
                  </Card>

                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Headers Customizados
                    </label>
                    <div className="space-y-2">
                      {Object.entries(operationFormData.headers).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <Input
                            value={key}
                            onChange={(e) => {
                              const newHeaders = { ...operationFormData.headers };
                              delete newHeaders[key];
                              newHeaders[e.target.value] = value;
                              handleOperationFormChange('headers', newHeaders);
                            }}
                            placeholder="Header-Name"
                            className="flex-1"
                          />
                          <Input
                            value={value}
                            onChange={(e) => handleMappingChange('headers', key, e.target.value)}
                            placeholder="valor"
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMappingDelete('headers', key)}
                            className="text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="secondary" size="sm" onClick={() => handleMappingAdd('headers')}>
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Header
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Mapping */}
              {operationEditorStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Request Mapping
                      <span className="text-surface-500 font-normal ml-2">
                        (campo interno → campo da API)
                      </span>
                    </label>
                    <div className="space-y-2">
                      {Object.entries(operationFormData.requestMapping).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <Input
                            value={key}
                            onChange={(e) => {
                              const newMapping = { ...operationFormData.requestMapping };
                              delete newMapping[key];
                              newMapping[e.target.value] = value;
                              handleOperationFormChange('requestMapping', newMapping);
                            }}
                            placeholder="campoInterno"
                            className="flex-1"
                          />
                          <ChevronRight className="w-4 h-4 text-surface-500" />
                          <Input
                            value={value}
                            onChange={(e) => handleMappingChange('requestMapping', key, e.target.value)}
                            placeholder="campo_api"
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMappingDelete('requestMapping', key)}
                            className="text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="secondary" size="sm" onClick={() => handleMappingAdd('requestMapping')}>
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Mapeamento
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Response Mapping
                      <span className="text-surface-500 font-normal ml-2">
                        (campo da API → campo interno)
                      </span>
                    </label>
                    <div className="space-y-2">
                      {Object.entries(operationFormData.responseMapping).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <Input
                            value={key}
                            onChange={(e) => {
                              const newMapping = { ...operationFormData.responseMapping };
                              delete newMapping[key];
                              newMapping[e.target.value] = value;
                              handleOperationFormChange('responseMapping', newMapping);
                            }}
                            placeholder="campo_api"
                            className="flex-1"
                          />
                          <ChevronRight className="w-4 h-4 text-surface-500" />
                          <Input
                            value={value}
                            onChange={(e) => handleMappingChange('responseMapping', key, e.target.value)}
                            placeholder="campoInterno"
                            className="flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMappingDelete('responseMapping', key)}
                            className="text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="secondary" size="sm" onClick={() => handleMappingAdd('responseMapping')}>
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar Mapeamento
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: Configuration */}
              {operationEditorStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-2">
                      Timeout (ms)
                    </label>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      {[
                        { value: 5000, label: '5s (Rápido)' },
                        { value: 15000, label: '15s (Normal)' },
                        { value: 30000, label: '30s (Padrão)' },
                        { value: 60000, label: '60s (Lento)' },
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => handleOperationFormChange('timeout', value)}
                          className={`p-2 rounded-lg border transition-all text-sm ${
                            operationFormData.timeout === value
                              ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                              : 'border-surface-700 text-surface-400 hover:border-surface-600'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      value={operationFormData.timeout}
                      onChange={(e) => handleOperationFormChange('timeout', parseInt(e.target.value) || 30000)}
                      placeholder="30000"
                      leftIcon={<Clock className="w-4 h-4" />}
                    />
                  </div>

                  <Card className="p-4 bg-surface-800/50">
                    <div className="flex items-center gap-2 mb-4">
                      <RefreshCw className="w-4 h-4 text-blue-400" />
                      <span className="font-medium text-surface-200">Configuração de Retry</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-surface-400 mb-1">
                          Máximo de Tentativas
                        </label>
                        <div className="grid grid-cols-4 gap-1">
                          {[0, 1, 3, 5].map((val) => (
                            <button
                              key={val}
                              onClick={() => handleOperationFormChange('retryConfig', { ...operationFormData.retryConfig, maxRetries: val })}
                              className={`p-2 rounded text-sm ${
                                operationFormData.retryConfig.maxRetries === val
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                              }`}
                            >
                              {val === 0 ? 'Nenhum' : `${val}x`}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-surface-400 mb-1">
                          Backoff (ms)
                        </label>
                        <div className="grid grid-cols-4 gap-1">
                          {[500, 1000, 2000, 5000].map((val) => (
                            <button
                              key={val}
                              onClick={() => handleOperationFormChange('retryConfig', { ...operationFormData.retryConfig, backoffMs: val })}
                              className={`p-2 rounded text-sm ${
                                operationFormData.retryConfig.backoffMs === val
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-surface-700 text-surface-400 hover:bg-surface-600'
                              }`}
                            >
                              {val >= 1000 ? `${val / 1000}s` : `${val}ms`}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Summary */}
                  <Card className="p-4 bg-gradient-to-br from-primary-500/10 to-accent-500/10 border-primary-500/20">
                    <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      Resumo da Operação
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-surface-500">Nome:</span>
                        <span className="text-surface-200 ml-2">{operationFormData.name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-surface-500">Método:</span>
                        <span className={`ml-2 ${getMethodColor(operationFormData.method).split(' ')[0]}`}>
                          {operationFormData.method}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-surface-500">Path:</span>
                        <span className="text-surface-200 ml-2 font-mono">{operationFormData.path}</span>
                      </div>
                      <div>
                        <span className="text-surface-500">Timeout:</span>
                        <span className="text-surface-200 ml-2">{operationFormData.timeout}ms</span>
                      </div>
                      <div>
                        <span className="text-surface-500">Retry:</span>
                        <span className="text-surface-200 ml-2">
                          {operationFormData.retryConfig.maxRetries}x / {operationFormData.retryConfig.backoffMs}ms
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-surface-700">
            <Button
              variant="secondary"
              onClick={() => {
                if (operationEditorStep === 1) {
                  setShowOperationEditor(false);
                  resetOperationForm();
                } else {
                  handleOperationPrevStep();
                }
              }}
            >
              {operationEditorStep === 1 ? 'Cancelar' : 'Voltar'}
            </Button>
            <div className="flex items-center gap-2">
              {operationEditorStep < 4 ? (
                <Button variant="primary" onClick={handleOperationNextStep}>
                  Próximo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button variant="primary" onClick={handleSaveOperation}>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {editingOperationName ? 'Salvar Alterações' : 'Criar Operação'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* CREDENTIAL FIELD EDITOR MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showCredentialEditor}
        onClose={() => {
          setShowCredentialEditor(false);
          resetCredentialFieldForm();
        }}
        title={editingCredentialField ? `Editar Campo: ${editingCredentialField}` : 'Novo Campo de Credencial'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Nome do Campo <span className="text-red-400">*</span>
            </label>
            <Input
              value={credentialFieldForm.name}
              onChange={(e) => handleCredentialFieldFormChange('name', e.target.value)}
              placeholder="apiKey, secretKey, accessToken..."
              error={credentialFieldErrors.name}
            />
            <p className="text-xs text-surface-500 mt-1">
              Identificador único do campo (camelCase). Ex: apiKey, webhookSecret
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Título <span className="text-red-400">*</span>
            </label>
            <Input
              value={credentialFieldForm.title}
              onChange={(e) => handleCredentialFieldFormChange('title', e.target.value)}
              placeholder="API Key, Secret Key, Access Token..."
              error={credentialFieldErrors.title}
            />
            <p className="text-xs text-surface-500 mt-1">
              Nome amigável exibido para o usuário
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Tipo de Dado
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'string', label: 'Texto' },
                { value: 'number', label: 'Número' },
                { value: 'boolean', label: 'Booleano' },
                { value: 'integer', label: 'Inteiro' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => handleCredentialFieldFormChange('type', value)}
                  className={`p-2 rounded-lg border transition-all text-sm ${
                    credentialFieldForm.type === value
                      ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                      : 'border-surface-700 text-surface-400 hover:border-surface-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Formato (opcional)
            </label>
            <Select
              value={credentialFieldForm.format}
              onChange={(e) => handleCredentialFieldFormChange('format', e.target.value)}
              options={[
                { value: '', label: 'Nenhum' },
                { value: 'password', label: '🔒 Password (oculto)' },
                { value: 'email', label: '📧 Email' },
                { value: 'uri', label: '🔗 URL' },
                { value: 'uuid', label: '🆔 UUID' },
                { value: 'date', label: '📅 Data' },
                { value: 'date-time', label: '📅 Data e Hora' },
              ]}
            />
            <p className="text-xs text-surface-500 mt-1">
              Use "password" para campos sensíveis como API keys e secrets
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Descrição
            </label>
            <textarea
              value={credentialFieldForm.description}
              onChange={(e) => handleCredentialFieldFormChange('description', e.target.value)}
              placeholder="Descreva o que este campo representa e onde encontrá-lo..."
              className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
            <div>
              <span className="font-medium text-surface-200">Campo Obrigatório</span>
              <p className="text-xs text-surface-500">O tenant deve preencher este campo</p>
            </div>
            <button
              onClick={() => handleCredentialFieldFormChange('required', !credentialFieldForm.required)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                credentialFieldForm.required ? 'bg-primary-500' : 'bg-surface-600'
              }`}
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  credentialFieldForm.required ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {/* Preview */}
          <Card className="p-4 bg-gradient-to-br from-primary-500/10 to-accent-500/10 border-primary-500/20">
            <h4 className="font-medium text-surface-100 mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary-400" />
              Preview do Campo
            </h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-surface-200">{credentialFieldForm.title || 'Título'}</span>
                {credentialFieldForm.required && (
                  <Badge variant="error" size="sm">Obrigatório</Badge>
                )}
              </div>
              <Input
                type={credentialFieldForm.format === 'password' ? 'password' : 'text'}
                placeholder={`Digite ${credentialFieldForm.title || 'o valor'}...`}
                disabled
              />
              {credentialFieldForm.description && (
                <p className="text-xs text-surface-500">{credentialFieldForm.description}</p>
              )}
            </div>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-700">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCredentialEditor(false);
                resetCredentialFieldForm();
              }}
            >
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSaveCredentialField}>
              <CheckCircle className="w-4 h-4 mr-1" />
              {editingCredentialField ? 'Salvar Alterações' : 'Adicionar Campo'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* CLONE CONFIRMATION MODAL */}
      {/* ============================================ */}
      <Modal
        isOpen={showCloneModal}
        onClose={handleCancelClone}
        title="Clonar Template"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <Copy className="w-8 h-8 text-blue-400" />
            <div>
              <p className="font-medium text-surface-100">
                Clonar "{cloneTargetTemplate?.name}"
              </p>
              <p className="text-sm text-surface-400">
                Uma cópia completa do template será criada com todas as configurações.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Nome do Novo Template <span className="text-red-400">*</span>
            </label>
            <Input
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Nome do template clonado..."
            />
            <p className="text-xs text-surface-500 mt-1">
              Escolha um nome único para identificar o template clonado
            </p>
          </div>

          <div className="p-3 bg-surface-800/50 rounded-lg">
            <h4 className="text-sm font-medium text-surface-300 mb-2">O que será copiado:</h4>
            <ul className="text-sm text-surface-400 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Configurações básicas (categoria, tipo, versão)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Configuração de autenticação
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Todas as operações e mapeamentos
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Schema de credenciais
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Configuração de webhooks
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-700">
            <Button variant="secondary" onClick={handleCancelClone} disabled={isCloning}>
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleConfirmClone}
              disabled={isCloning || !cloneName.trim()}
            >
              {isCloning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Clonando...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Confirmar Clone
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* NEW TEMPLATE MODAL (WIZARD) */}
      {/* ============================================ */}
      <Modal
        isOpen={showNewTemplateModal}
        onClose={handleCloseNewTemplateModal}
        title="Criar Novo Template"
        size="xl"
      >
        <div className="space-y-6">
          {/* Wizard Steps Indicator */}
          <div className="flex items-center justify-between mb-6">
            {[
              { step: 1, label: 'Básico', icon: Info },
              { step: 2, label: 'Autenticação', icon: Key },
              { step: 3, label: 'Operações', icon: Activity },
              { step: 4, label: 'Webhooks', icon: Webhook },
              { step: 5, label: 'Revisão', icon: CheckCircle },
            ].map(({ step, label, icon: Icon }, index) => (
              <div key={step} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      newTemplateStep === step
                        ? 'bg-primary-500 text-white'
                        : newTemplateStep > step
                        ? 'bg-green-500 text-white'
                        : 'bg-surface-700 text-surface-400'
                    }`}
                  >
                    {newTemplateStep > step ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-1 ${newTemplateStep === step ? 'text-primary-400' : 'text-surface-500'}`}>
                    {label}
                  </span>
                </div>
                {index < 4 && (
                  <div className={`w-16 h-0.5 mx-2 ${newTemplateStep > step ? 'bg-green-500' : 'bg-surface-700'}`} />
                )}
              </div>
            ))}
          </div>

          {/* General Error */}
          {newTemplateErrors._general && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              {newTemplateErrors._general}
            </div>
          )}

          {/* Step 1: Basic Info */}
          {newTemplateStep === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <Info className="w-5 h-5 text-primary-400" />
                Informações Básicas
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Nome do Template <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={newTemplateData.name}
                    onChange={(e) => handleNewTemplateFormChange('name', e.target.value)}
                    placeholder="Ex: OpenPix Pix, Asaas Payments..."
                    error={newTemplateErrors.name}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Descrição
                  </label>
                  <textarea
                    value={newTemplateData.description}
                    onChange={(e) => handleNewTemplateFormChange('description', e.target.value)}
                    placeholder="Descreva o que este template faz..."
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Categoria <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                      <button
                        key={key}
                        onClick={() => handleNewTemplateFormChange('category', key)}
                        className={`p-3 rounded-lg border transition-all text-center ${
                          newTemplateData.category === key
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        }`}
                      >
                        <span className="text-2xl">{info.emoji}</span>
                        <p className={`text-sm mt-1 ${newTemplateData.category === key ? 'text-primary-400' : 'text-surface-400'}`}>
                          {info.label.split(' ')[0]}
                        </p>
                      </button>
                    ))}
                  </div>
                  {newTemplateErrors.category && (
                    <p className="text-red-400 text-sm mt-1">{newTemplateErrors.category}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Tipo <span className="text-red-400">*</span>
                  </label>
                  <Select
                    value={newTemplateData.type}
                    onChange={(e) => handleNewTemplateFormChange('type', e.target.value)}
                    options={TYPE_OPTIONS.filter(o => o.value)}
                    error={newTemplateErrors.type}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Versão <span className="text-red-400">*</span>
                  </label>
                  <Input
                    value={newTemplateData.version}
                    onChange={(e) => handleNewTemplateFormChange('version', e.target.value)}
                    placeholder="1.0.0"
                    error={newTemplateErrors.version}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Status Inicial
                  </label>
                  <Select
                    value={newTemplateData.status}
                    onChange={(e) => handleNewTemplateFormChange('status', e.target.value)}
                    options={STATUS_OPTIONS.filter(o => o.value)}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Authentication */}
          {newTemplateStep === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <Key className="w-5 h-5 text-primary-400" />
                Configuração de Autenticação
              </h3>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Base URL
                </label>
                <Input
                  value={newTemplateData.baseUrl}
                  onChange={(e) => handleNewTemplateFormChange('baseUrl', e.target.value)}
                  placeholder="https://api.provider.com/v1"
                  error={newTemplateErrors.baseUrl}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Tipo de Autenticação <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(AUTH_TYPE_INFO).map(([key, info]) => {
                    const AuthIcon = info.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => handleNewTemplateFormChange('authConfig.type', key)}
                        className={`p-3 rounded-lg border transition-all text-left ${
                          newTemplateData.authConfig.type === key
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-surface-700 hover:border-surface-600'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AuthIcon className={`w-5 h-5 ${info.color}`} />
                          <span className="font-medium text-surface-200">{info.label}</span>
                        </div>
                        <p className="text-xs text-surface-500 mt-1">{info.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Conditional fields based on auth type */}
              {newTemplateData.authConfig.type === 'API_KEY' && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Header Name
                    </label>
                    <Input
                      value={newTemplateData.authConfig.apiKeyHeader}
                      onChange={(e) => handleNewTemplateFormChange('authConfig.apiKeyHeader', e.target.value)}
                      placeholder="X-API-Key"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Prefixo (opcional)
                    </label>
                    <Input
                      value={newTemplateData.authConfig.prefix}
                      onChange={(e) => handleNewTemplateFormChange('authConfig.prefix', e.target.value)}
                      placeholder="Bearer, Basic..."
                    />
                  </div>
                </div>
              )}

              {newTemplateData.authConfig.type === 'OAUTH2' && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-surface-800/50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Token URL
                    </label>
                    <Input
                      value={newTemplateData.authConfig.tokenUrl}
                      onChange={(e) => handleNewTemplateFormChange('authConfig.tokenUrl', e.target.value)}
                      placeholder="https://api.provider.com/oauth/token"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-300 mb-1">
                      Grant Type
                    </label>
                    <Select
                      value={newTemplateData.authConfig.grantType}
                      onChange={(e) => handleNewTemplateFormChange('authConfig.grantType', e.target.value)}
                      options={[
                        { value: 'client_credentials', label: 'Client Credentials' },
                        { value: 'authorization_code', label: 'Authorization Code' },
                        { value: 'password', label: 'Password' },
                        { value: 'refresh_token', label: 'Refresh Token' },
                      ]}
                    />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3: Operations */}
          {newTemplateStep === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary-400" />
                  Operações
                </h3>
                <Button variant="secondary" size="sm" onClick={() => {
                  // Add a sample operation
                  const opName = `operation_${Object.keys(newTemplateData.operations).length + 1}`;
                  handleNewTemplateFormChange('operations', {
                    ...newTemplateData.operations,
                    [opName]: {
                      method: 'GET',
                      path: '/endpoint',
                      description: 'Nova operação',
                      timeout: 30000,
                      retryConfig: { maxRetries: 3, backoffMs: 1000 },
                    },
                  });
                }}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Operação
                </Button>
              </div>

              {Object.keys(newTemplateData.operations).length === 0 ? (
                <Card className="p-8 text-center">
                  <Activity className="w-12 h-12 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400 mb-2">Nenhuma operação configurada</p>
                  <p className="text-sm text-surface-500">
                    Operações definem os endpoints que o template pode chamar.
                    Você pode adicionar operações agora ou depois de criar o template.
                  </p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {Object.entries(newTemplateData.operations).map(([opName, op]) => (
                    <Card key={opName} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold border ${getMethodColor(op.method)}`}>
                            {op.method}
                          </span>
                          <span className="font-medium text-surface-100">{opName}</span>
                          <span className="text-surface-400 font-mono text-sm">{op.path}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newOps = { ...newTemplateData.operations };
                            delete newOps[opName];
                            handleNewTemplateFormChange('operations', newOps);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              <p className="text-sm text-surface-500">
                💡 Dica: Você pode configurar operações detalhadamente após criar o template usando o editor de operações.
              </p>
            </motion.div>
          )}

          {/* Step 4: Webhooks */}
          {newTemplateStep === 4 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <Webhook className="w-5 h-5 text-primary-400" />
                Configuração de Webhooks
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Header de Assinatura
                  </label>
                  <Input
                    value={newTemplateData.webhookConfig.signatureHeader}
                    onChange={(e) => handleNewTemplateFormChange('webhookConfig', {
                      ...newTemplateData.webhookConfig,
                      signatureHeader: e.target.value,
                    })}
                    placeholder="X-Webhook-Signature"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Algoritmo de Assinatura
                  </label>
                  <Select
                    value={newTemplateData.webhookConfig.signatureAlgorithm}
                    onChange={(e) => handleNewTemplateFormChange('webhookConfig', {
                      ...newTemplateData.webhookConfig,
                      signatureAlgorithm: e.target.value,
                    })}
                    options={[
                      { value: 'sha256', label: 'SHA-256' },
                      { value: 'sha512', label: 'SHA-512' },
                      { value: 'sha1', label: 'SHA-1' },
                      { value: 'md5', label: 'MD5' },
                    ]}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Eventos Suportados
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newWebhookEvent}
                    onChange={(e) => setNewWebhookEvent(e.target.value)}
                    placeholder="PAYMENT_RECEIVED, CHARGE_COMPLETED..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newWebhookEvent.trim()) {
                          handleNewTemplateFormChange('webhookConfig', {
                            ...newTemplateData.webhookConfig,
                            events: [...newTemplateData.webhookConfig.events, newWebhookEvent.trim()],
                          });
                          setNewWebhookEvent('');
                        }
                      }
                    }}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (newWebhookEvent.trim()) {
                        handleNewTemplateFormChange('webhookConfig', {
                          ...newTemplateData.webhookConfig,
                          events: [...newTemplateData.webhookConfig.events, newWebhookEvent.trim()],
                        });
                        setNewWebhookEvent('');
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {newTemplateData.webhookConfig.events.map((event) => (
                    <Badge key={event} variant="default" className="flex items-center gap-1">
                      {event}
                      <button
                        onClick={() => handleNewTemplateFormChange('webhookConfig', {
                          ...newTemplateData.webhookConfig,
                          events: newTemplateData.webhookConfig.events.filter(e => e !== event),
                        })}
                        className="ml-1 hover:text-red-400"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 5: Review */}
          {newTemplateStep === 5 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <h3 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Revisão Final
              </h3>

              <Card className="p-4 bg-gradient-to-br from-primary-500/10 to-accent-500/10 border-primary-500/20">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Nome</label>
                    <p className="text-surface-100 font-medium">{newTemplateData.name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Versão</label>
                    <p className="text-surface-100 font-medium">v{newTemplateData.version}</p>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Categoria</label>
                    <Badge className={`bg-gradient-to-r ${getCategoryInfo(newTemplateData.category).gradient} text-white`}>
                      {getCategoryInfo(newTemplateData.category).emoji} {getCategoryInfo(newTemplateData.category).label}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Tipo</label>
                    <p className={getTypeInfo(newTemplateData.type).color}>
                      {getTypeInfo(newTemplateData.type).label}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Autenticação</label>
                    <p className={getAuthInfo(newTemplateData.authConfig.type).color}>
                      {getAuthInfo(newTemplateData.authConfig.type).label}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Status</label>
                    <Badge className={`${getStatusInfo(newTemplateData.status).bgColor} ${getStatusInfo(newTemplateData.status).color}`}>
                      {getStatusInfo(newTemplateData.status).emoji} {getStatusInfo(newTemplateData.status).label}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-surface-500 uppercase">Base URL</label>
                    <p className="text-surface-100 font-mono text-sm">{newTemplateData.baseUrl || 'Não configurada'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Operações</label>
                    <p className="text-surface-100">{Object.keys(newTemplateData.operations).length} configuradas</p>
                  </div>
                  <div>
                    <label className="text-xs text-surface-500 uppercase">Eventos Webhook</label>
                    <p className="text-surface-100">{newTemplateData.webhookConfig.events.length} configurados</p>
                  </div>
                </div>
              </Card>

              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-sm text-surface-200">
                    Após criar o template, você poderá editar todas as configurações em detalhes,
                    incluindo operações, mapeamentos e schema de credenciais.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-surface-700">
            <Button
              variant="secondary"
              onClick={newTemplateStep === 1 ? handleCloseNewTemplateModal : handleNewTemplatePrevStep}
            >
              {newTemplateStep === 1 ? 'Cancelar' : 'Voltar'}
            </Button>
            <div className="flex items-center gap-2">
              {newTemplateStep < 5 ? (
                <Button variant="primary" onClick={handleNewTemplateNextStep}>
                  Próximo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleCreateTemplate}
                  disabled={isCreatingTemplate}
                >
                  {isCreatingTemplate ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Criar Template
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ============================================ */}
      {/* DOCUMENTATION MODAL (Enhanced Edit) */}
      {/* ============================================ */}
      {selectedTemplate && (
        <TemplateEditModal
          isOpen={showDocumentationModal}
          onClose={() => {
            setShowDocumentationModal(false);
            setTemplateCompleteness(null);
          }}
          template={selectedTemplate}
          completeness={templateCompleteness || undefined}
          availableTemplates={templates.map(t => ({ id: t.id, name: t.name, description: t.description }))}
          onSave={handleSaveDocumentation}
          onToggleVisibility={handleToggleVisibilityFromModal}
        />
      )}
    </div>
  );
}
